import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Header } from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { Download, TrendingUp, DollarSign, Plus, Target, Settings, X, Users, ChevronRight, ChevronLeft, Link2, ExternalLink, Copy, Pencil, Trash2 as Trash, BarChart3, Clock, AlertCircle, FileText, Zap, Pin, Search, Bell, CheckSquare, ListChecks, Activity, Sliders, ArrowUpRight, Check, Repeat, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RichTextEditor } from '../components/RichTextEditor';
import { PipelinePulse } from '../components/PipelinePulse';
import { CustomFieldsRenderer, validateCustomFields } from '../components/CustomFieldsRenderer';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const startOfDay = (d) => { const c = new Date(d); c.setHours(0, 0, 0, 0); return c; };

const stageColorPalette = [
  { accent: '#4f46e5', light: '#eef2ff', border: '#c7d2fe' },  // indigo
  { accent: '#7c3aed', light: '#f5f3ff', border: '#ddd6fe' },  // violet
  { accent: '#9333ea', light: '#faf5ff', border: '#e9d5ff' },  // purple
  { accent: '#d97706', light: '#fffbeb', border: '#fde68a' },  // amber
  { accent: '#0891b2', light: '#ecfeff', border: '#a5f3fc' },  // cyan
  { accent: '#0d9488', light: '#f0fdfa', border: '#99f6e4' },  // teal
  { accent: '#e11d48', light: '#fff1f2', border: '#fecdd3' },  // rose
  { accent: '#ea580c', light: '#fff7ed', border: '#fed7aa' },  // orange
  { accent: '#2563eb', light: '#eff6ff', border: '#bfdbfe' },  // blue
  { accent: '#c026d3', light: '#fdf4ff', border: '#f0abfc' },  // fuchsia
];

const specialStageColors = {
  'closed_won': { accent: '#059669', light: '#ecfdf5', border: '#a7f3d0' },
  'closed_lost': { accent: '#dc2626', light: '#fef2f2', border: '#fecaca' },
};

const getStageColorByIndex = (stage, index) => {
  const nameLower = (stage.name || '').toLowerCase().replace(/\s+/g, '_');
  if (specialStageColors[nameLower]) return specialStageColors[nameLower];
  if (specialStageColors[stage.id]) return specialStageColors[stage.id];
  return stageColorPalette[index % stageColorPalette.length];
};

const PinnedTodoEditor = ({ content, sectionName, onSave }) => {
  const saveTimeoutRef = useRef(null);
  const isSortingRef = useRef(false);
  const prevCheckedRef = useRef('');

  const sortTaskListJSON = (json) => {
    if (!json) return json;
    const walk = (node) => {
      if (node.type === 'taskList' && node.content) {
        const unchecked = node.content.filter(c => !c.attrs?.checked);
        const checked = node.content.filter(c => c.attrs?.checked);
        node.content = [...unchecked, ...checked];
      }
      if (node.content) node.content.forEach(walk);
    };
    walk(json);
    return json;
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      TaskList,
      TaskItem.configure({ nested: true }),
    ],
    content: content || '<ul data-type="taskList"><li data-type="taskItem" data-checked="false"><label><input type="checkbox"><span></span></label><div><p></p></div></li></ul>',
    onCreate: ({ editor: ed }) => {
      const states = [];
      ed.state.doc.descendants((node) => {
        if (node.type.name === 'taskItem') states.push(node.attrs.checked ? '1' : '0');
      });
      prevCheckedRef.current = states.join(',');
    },
    onUpdate: ({ editor: ed }) => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        onSave(sectionName, ed.getHTML());
      }, 800);
    },
  });

  useEffect(() => {
    if (editor && content && editor.getHTML() !== content) {
      editor.commands.setContent(content);
    }
  }, [content]);

  if (!editor) return null;

  return (
    <div className="dashboard-pinned-editor" data-testid="pinned-todo-editor">
      <EditorContent editor={editor} className="prose prose-sm max-w-none focus:outline-none min-h-[80px] text-sm [&_.tiptap]:outline-none [&_.tiptap]:min-h-[80px] [&_ul[data-type=taskList]]:list-none [&_ul[data-type=taskList]]:pl-0 [&_li[data-type=taskItem]]:flex [&_li[data-type=taskItem]]:items-start [&_li[data-type=taskItem]]:gap-2 [&_li[data-type=taskItem]_label]:mt-0.5 [&_li[data-type=taskItem]_input]:mt-0 [&_li[data-type=taskItem]_div]:flex-1 [&_li[data-type=taskItem]_p]:m-0" />
    </div>
  );
};

const ManagerDashboard = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [selectedRep, setSelectedRep] = useState(null);
  const [repData, setRepData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingQuota, setEditingQuota] = useState(false);
  const [teamQuotaInput, setTeamQuotaInput] = useState('');
  const [dealSearch, setDealSearch] = useState('');
  const [dealStageFilter, setDealStageFilter] = useState('all');
  const [onboarding, setOnboarding] = useState(null);
  const [formTemplates, setFormTemplates] = useState([]);
  const [quickFormDeal, setQuickFormDeal] = useState(null);
  const [quickFormTemplate, setQuickFormTemplate] = useState('');
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchOverview();
    fetchFormTemplates();
    fetchAnalytics();
  }, []);

  const fetchFormTemplates = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/form-templates`, { withCredentials: true });
      setFormTemplates(data);
    } catch {}
  };

  const fetchAnalytics = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/team/analytics`, { withCredentials: true });
      setAnalytics(data);
    } catch {}
  };

  const handleQuickFormAssign = async (dealId) => {
    if (!quickFormTemplate || !dealId) {
      toast.error('Select a form template');
      return;
    }
    // Find the rep who owns this deal
    const repId = selectedRep;
    if (!repId) {
      toast.error('Select a rep first');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/form-assignments`, {
        template_id: quickFormTemplate,
        account_id: dealId,
        assigned_to: repId,
      }, { withCredentials: true });
      toast.success('Form assigned!');
      setQuickFormDeal(null);
      setQuickFormTemplate('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign form');
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    try {
      const [overviewRes, onboardRes] = await Promise.all([
        axios.get(`${API_URL}/api/team/overview`, { withCredentials: true }),
        axios.get(`${API_URL}/api/onboarding-status`, { withCredentials: true }),
      ]);
      setOverview(overviewRes.data);
      setTeamQuotaInput(overviewRes.data.team.quota_amount || '');
      if (!onboardRes.data.all_done) setOnboarding(onboardRes.data);
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const loadRepData = async (repId) => {
    setSelectedRep(repId);
    setDealSearch('');
    setDealStageFilter('all');
    try {
      const { data } = await axios.get(`${API_URL}/api/team/rep-data/${repId}`, { withCredentials: true });
      setRepData(data);
    } catch {
      toast.error('Failed to load rep data');
    }
  };

  const saveTeamQuota = async () => {
    try {
      await axios.put(`${API_URL}/api/team/quota`, { quota_amount: Number(teamQuotaInput) || 0 }, { withCredentials: true });
      toast.success('Team quota updated');
      setEditingQuota(false);
      fetchOverview();
    } catch {
      toast.error('Failed to save');
    }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);

  const team = overview?.team;
  const reps = overview?.reps || [];

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 md:ml-64 flex flex-col overflow-hidden">
        <Header />
        <div className="flex-1 overflow-auto">
          <div className="p-4 md:p-8">
            <div className="mb-8">
              <h1 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Manager Dashboard
              </h1>
              <p className="text-base text-slate-600">View your team's pipeline and performance</p>
            </div>

            {/* Getting Started Checklist */}
            {onboarding && (
              <div className="mb-6 md:mb-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Welcome to deal desk
                    </h2>
                    <p className="text-sm text-slate-500 mt-0.5">Complete these steps to get started</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-indigo-600">{onboarding.completed}/{onboarding.total}</span>
                    <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${(onboarding.completed / onboarding.total) * 100}%` }} />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  {onboarding.steps.map((step) => (
                    <button
                      key={step.key}
                      onClick={() => !step.done && navigate(step.link)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                        step.done
                          ? 'bg-emerald-50 border border-emerald-200'
                          : 'bg-slate-50 border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 cursor-pointer'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                        step.done ? 'bg-emerald-500' : 'border-2 border-slate-300'
                      }`}>
                        {step.done && <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                      </div>
                      <span className={`text-sm font-medium ${step.done ? 'text-emerald-700 line-through' : 'text-slate-700'}`}>
                        {step.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : reps.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
                <Users size={40} className="mx-auto mb-4 text-slate-300" />
                <p className="text-slate-500 font-medium mb-2">No reps linked yet</p>
                <p className="text-sm text-slate-400 mb-4">Go to Settings to link your team members by email</p>
                <Button onClick={() => navigate('/settings')} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-6 font-semibold">
                  Go to Settings
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Team Quota Tracking */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <Target size={20} className="text-indigo-600" />
                      </div>
                      <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Team Quota</h2>
                    </div>
                    {!editingQuota ? (
                      <button
                        data-testid="edit-team-quota-btn"
                        onClick={() => setEditingQuota(true)}
                        className="text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
                      >
                        {team?.quota_amount ? 'Edit' : 'Set Quota'}
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-500">$</span>
                        <Input
                          data-testid="team-quota-input"
                          type="number"
                          value={teamQuotaInput}
                          onChange={(e) => setTeamQuotaInput(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && saveTeamQuota()}
                          className="w-40 rounded-lg border-slate-200 text-sm"
                          autoFocus
                        />
                        <Button data-testid="save-team-quota-btn" onClick={saveTeamQuota} size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs px-3">Save</Button>
                        <button onClick={() => setEditingQuota(false)} className="text-xs text-slate-500 hover:text-slate-700">Cancel</button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-6 mb-4">
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Closed Won</p>
                      <p className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(team?.total_closed_won || 0)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Total Pipeline</p>
                      <p className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(team?.total_pipeline_arr || 0)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500 mb-1">Team Quota</p>
                      <p className="text-2xl font-bold text-slate-600">{team?.quota_amount ? formatCurrency(team.quota_amount) : 'Not set'}</p>
                    </div>
                  </div>

                  {team?.quota_amount > 0 && (
                    <>
                      <div className="w-full h-5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${team.progress_percent >= 100 ? 'bg-emerald-500' : team.progress_percent >= 75 ? 'bg-indigo-500' : team.progress_percent >= 50 ? 'bg-amber-500' : 'bg-red-400'}`}
                          style={{ width: `${Math.min(team.progress_percent, 100)}%` }}
                        />
                      </div>
                      <p className={`text-sm font-bold mt-2 ${team.progress_percent >= 100 ? 'text-emerald-600' : 'text-slate-600'}`}>
                        {Math.round(team.progress_percent)}% of team quota
                      </p>
                    </>
                  )}
                </div>

                {/* Team Analytics */}
                {analytics && analytics.reps.length > 0 && (
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="team-analytics-section">
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                        <BarChart3 size={20} className="text-violet-600" />
                      </div>
                      <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Team Analytics</h2>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                      <div className="bg-indigo-50 rounded-xl p-3 border border-indigo-100">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-500">Total Deals</p>
                        <p className="text-xl font-black text-indigo-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{analytics.totals.total_deals}</p>
                      </div>
                      <div className="bg-violet-50 rounded-xl p-3 border border-violet-100">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-violet-500">Pipeline</p>
                        <p className="text-xl font-black text-violet-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(analytics.totals.total_pipeline_arr)}</p>
                      </div>
                      <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-500">Closed Won</p>
                        <p className="text-xl font-black text-emerald-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(analytics.totals.total_closed_won)}</p>
                      </div>
                      <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600">Avg Deal Size</p>
                        <p className="text-xl font-black text-amber-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(analytics.totals.avg_deal_size)}</p>
                      </div>
                      <div className="bg-cyan-50 rounded-xl p-3 border border-cyan-100">
                        <p className="text-[10px] font-bold uppercase tracking-wider text-cyan-600">Win Rate</p>
                        <p className="text-xl font-black text-cyan-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{analytics.totals.team_win_rate}%</p>
                      </div>
                    </div>

                    {/* Rep Performance Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="analytics-rep-table">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Rep</th>
                            <th className="text-right py-2 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Deals</th>
                            <th className="text-right py-2 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Active</th>
                            <th className="text-right py-2 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Won</th>
                            <th className="text-right py-2 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Pipeline</th>
                            <th className="text-right py-2 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Closed Won</th>
                            <th className="text-right py-2 px-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Win Rate</th>
                          </tr>
                        </thead>
                        <tbody>
                          {analytics.reps.map((r) => (
                            <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                              <td className="py-2.5 px-3 font-semibold text-slate-900">{r.name}</td>
                              <td className="py-2.5 px-3 text-right text-slate-600">{r.total_deals}</td>
                              <td className="py-2.5 px-3 text-right text-slate-600">{r.active_deals}</td>
                              <td className="py-2.5 px-3 text-right text-slate-600">{r.closed_won_count}</td>
                              <td className="py-2.5 px-3 text-right font-semibold text-violet-600">{formatCurrency(r.pipeline_arr)}</td>
                              <td className="py-2.5 px-3 text-right font-bold text-emerald-600">{formatCurrency(r.closed_won_arr)}</td>
                              <td className="py-2.5 px-3 text-right">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                  r.win_rate >= 50 ? 'bg-emerald-50 text-emerald-700' :
                                  r.win_rate >= 25 ? 'bg-amber-50 text-amber-700' :
                                  'bg-slate-100 text-slate-600'
                                }`}>{r.win_rate}%</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Rep Cards with Quota Attainment */}
                <div>
                  <h2 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Team Members</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {reps.map((rep) => (
                      <button
                        key={rep.id}
                        data-testid={`rep-card-${rep.id}`}
                        onClick={() => loadRepData(rep.id)}
                        className={`bg-white rounded-2xl border shadow-sm p-5 text-left transition-all hover:shadow-md ${
                          selectedRep === rep.id ? 'border-indigo-500 ring-2 ring-indigo-100' : 'border-slate-200 hover:border-indigo-200'
                        }`}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          {rep.avatar_url ? (
                            <img src={rep.avatar_url} alt={rep.name} className="w-10 h-10 rounded-full object-cover" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                              <span className="text-sm font-bold text-indigo-600">{(rep.name || 'U')[0].toUpperCase()}</span>
                            </div>
                          )}
                          <div>
                            <p className="font-semibold text-sm text-slate-900">{rep.name}</p>
                            <p className="text-xs text-slate-500">{rep.deal_count} deals</p>
                          </div>
                        </div>
                        <div className="flex justify-between text-xs mb-2">
                          <span className="text-slate-500">Closed Won: <span className="font-bold text-slate-800">{formatCurrency(rep.closed_won_arr)}</span></span>
                          {rep.quota_amount > 0 && (
                            <span className={`font-bold ${rep.progress_percent >= 100 ? 'text-emerald-600' : rep.progress_percent >= 75 ? 'text-indigo-600' : 'text-amber-600'}`}>
                              {Math.round(rep.progress_percent)}%
                            </span>
                          )}
                        </div>
                        {rep.quota_amount > 0 && (
                          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${rep.progress_percent >= 100 ? 'bg-emerald-500' : rep.progress_percent >= 75 ? 'bg-indigo-500' : 'bg-amber-500'}`}
                              style={{ width: `${Math.min(rep.progress_percent, 100)}%` }}
                            />
                          </div>
                        )}
                        {rep.quota_amount === 0 && (
                          <p className="text-xs text-slate-400">No quota set</p>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Selected Rep Detail */}
                {repData && (
                  <div className="space-y-6">
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                      <div className="flex items-center gap-4">
                        {repData.rep.avatar_url ? (
                          <img src={repData.rep.avatar_url} alt={repData.rep.name} className="w-14 h-14 rounded-full object-cover" />
                        ) : (
                          <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-xl font-bold text-indigo-600">{(repData.rep.name || 'U')[0].toUpperCase()}</span>
                          </div>
                        )}
                        <div>
                          <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{repData.rep.name}</h2>
                          <p className="text-sm text-slate-500">{repData.rep.email}</p>
                        </div>
                      </div>
                    </div>

                    {repData.quota && (
                      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                            <Target size={20} className="text-indigo-600" />
                          </div>
                          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Individual Quota</h3>
                        </div>
                        <div className="flex justify-between mb-3">
                          <div>
                            <p className="text-sm text-slate-500">Closed Won</p>
                            <p className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(repData.quota.closed_won_arr)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-slate-500">Quota</p>
                            <p className="text-xl font-bold text-slate-600">{formatCurrency(repData.quota.quota_amount)}</p>
                          </div>
                        </div>
                        <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${repData.quota.progress_percent >= 100 ? 'bg-emerald-500' : repData.quota.progress_percent >= 75 ? 'bg-indigo-500' : 'bg-amber-500'}`}
                            style={{ width: `${Math.min(repData.quota.progress_percent, 100)}%` }}
                          />
                        </div>
                        <p className={`text-sm font-bold mt-2 ${repData.quota.progress_percent >= 100 ? 'text-emerald-600' : 'text-slate-600'}`}>
                          {Math.round(repData.quota.progress_percent)}% of quota
                        </p>
                      </div>
                    )}

                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                      <h3 className="text-lg font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Pipeline Overview</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {repData.stages.map((stage, stageIdx) => {
                          const deals = repData.deals.filter(d => d.stage_id === stage.id);
                          const arr = deals.reduce((s, d) => s + (d.arr || 0), 0);
                          const colorScheme = getStageColorByIndex(stage, stageIdx);
                          return (
                            <div key={stage.id} className="p-4 rounded-xl border" style={{ borderColor: colorScheme.border, backgroundColor: colorScheme.light }}>
                              <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: colorScheme.accent }}>{stage.name}</p>
                              <p className="text-xl font-black text-slate-900">{deals.length}</p>
                              <p className="text-xs text-slate-500">{formatCurrency(arr)}</p>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                        <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>All Deals ({repData.deals.length})</h3>
                        <div className="flex gap-2 flex-1 md:max-w-md">
                          <Input
                            data-testid="mgr-deal-search"
                            placeholder="Search deals..."
                            value={dealSearch}
                            onChange={(e) => setDealSearch(e.target.value)}
                            className="rounded-lg border-slate-200 text-sm flex-1"
                          />
                          <select
                            data-testid="mgr-stage-filter"
                            value={dealStageFilter}
                            onChange={(e) => setDealStageFilter(e.target.value)}
                            className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none bg-white"
                          >
                            <option value="all">All Stages</option>
                            {repData.stages.map(s => (
                              <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        {repData.deals
                          .filter(deal => {
                            const q = dealSearch.toLowerCase();
                            const matchesSearch = !q || deal.account_name?.toLowerCase().includes(q) || deal.main_contact_name?.toLowerCase().includes(q) || deal.notes?.toLowerCase().includes(q);
                            const matchesStage = dealStageFilter === 'all' || deal.stage_id === dealStageFilter;
                            return matchesSearch && matchesStage;
                          })
                          .map((deal) => {
                          const stageIdx = repData.stages.findIndex(s => s.id === deal.stage_id);
                          const colorScheme = stageIdx >= 0 ? getStageColorByIndex(repData.stages[stageIdx], stageIdx) : { accent: '#64748b', light: '#f8fafc', border: '#e2e8f0' };
                          return (
                          <div key={deal.id} data-testid={`mgr-deal-${deal.id}`} className="flex items-center justify-between p-4 bg-white rounded-xl border border-slate-200 hover:border-indigo-200 hover:shadow-sm transition-all group">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <p className="font-semibold text-sm text-slate-900 truncate">{deal.account_name}</p>
                                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: colorScheme.light, color: colorScheme.accent, border: `1px solid ${colorScheme.border}` }}>
                                  {deal.stage_name}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500">{deal.employee_count} EE · {deal.main_contact_name || 'No contact'}</p>
                            </div>
                            <div className="flex items-center gap-3 flex-shrink-0">
                              <div className="text-right">
                                <p className="text-base font-black text-emerald-600" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(deal.arr || 0)}</p>
                                <p className="text-[10px] text-slate-400">ARR</p>
                              </div>
                              {formTemplates.length > 0 && (
                                <Popover open={quickFormDeal === deal.id} onOpenChange={(o) => { if (o) { setQuickFormDeal(deal.id); setQuickFormTemplate(''); } else { setQuickFormDeal(null); } }}>
                                  <PopoverTrigger asChild>
                                    <button
                                      data-testid={`mgr-deal-assign-form-${deal.id}`}
                                      className="p-2 rounded-lg text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-colors opacity-0 group-hover:opacity-100"
                                      title="Assign Form"
                                    >
                                      <ListChecks size={16} />
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-60 p-3 rounded-xl" align="end">
                                    <p className="text-xs font-bold text-slate-900 mb-2">Assign Form</p>
                                    <select
                                      data-testid={`mgr-deal-form-select-${deal.id}`}
                                      value={quickFormTemplate}
                                      onChange={(e) => setQuickFormTemplate(e.target.value)}
                                      className="w-full px-2.5 py-2 rounded-lg border border-slate-200 text-xs focus:outline-none focus:border-violet-500 bg-white mb-2"
                                    >
                                      <option value="">Select template...</option>
                                      {formTemplates.map(t => (
                                        <option key={t.id} value={t.id}>{t.name} ({t.cadence})</option>
                                      ))}
                                    </select>
                                    <button
                                      data-testid={`mgr-deal-form-assign-btn-${deal.id}`}
                                      onClick={() => handleQuickFormAssign(deal.id)}
                                      disabled={!quickFormTemplate}
                                      className="w-full bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg py-1.5 text-xs font-semibold transition-colors"
                                    >
                                      Assign to {deal.account_name.length > 15 ? deal.account_name.slice(0, 15) + '...' : deal.account_name}
                                    </button>
                                  </PopoverContent>
                                </Popover>
                              )}
                              <button onClick={() => navigate(`/account/${deal.id}`)} className="text-xs font-semibold text-indigo-600 hover:text-indigo-700">View</button>
                            </div>
                          </div>
                          );
                        })}
                        {repData.deals.filter(deal => {
                          const q = dealSearch.toLowerCase();
                          const matchesSearch = !q || deal.account_name?.toLowerCase().includes(q) || deal.main_contact_name?.toLowerCase().includes(q) || deal.notes?.toLowerCase().includes(q);
                          const matchesStage = dealStageFilter === 'all' || deal.stage_id === dealStageFilter;
                          return matchesSearch && matchesStage;
                        }).length === 0 && <p className="text-sm text-slate-400 text-center py-4">No deals match your search</p>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [stats, setStats] = useState({ stats: [], total_arr: 0 });
  const [loading, setLoading] = useState(true);
  const [quota, setQuota] = useState(null);
  const [showQuotaSettings, setShowQuotaSettings] = useState(false);
  const [selectedFy, setSelectedFy] = useState(null);
  const [showSimulator, setShowSimulator] = useState(false);
  const [simDeals, setSimDeals] = useState([]);
  const [simStageFilter, setSimStageFilter] = useState([]);
  const [quotaForm, setQuotaForm] = useState({
    q1: '',
    q2: '',
    q3: '',
    q4: '',
    h1: '',
    h2: '',
    h3: '',
    h4: '',
    fiscal_year_start_month: '1',
    quarter_months: '3',
    commission_rate: '0',
    multipliers: [],
    spiff_bonuses: [],
    quarterly_commission_overrides: {}
  });
  const [showAddDeal, setShowAddDeal] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const [partners, setPartners] = useState([]);
  const [onboarding, setOnboarding] = useState(null);
  const [quickLinks, setQuickLinks] = useState([]);
  const [activityFeed, setActivityFeed] = useState([]);
  const [todaysFocus, setTodaysFocus] = useState(null);
  const [dashboardTodos, setDashboardTodos] = useState([]);
  const [showAddLink, setShowAddLink] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [linkForm, setLinkForm] = useState({ name: '', url: '' });
  const [dashboardSearch, setDashboardSearch] = useState('');
  const [allAccounts, setAllAccounts] = useState([]);
  const [commissionsSummary, setCommissionsSummary] = useState(null);
  const [dashReminders, setDashReminders] = useState([]);
  const [dashTodoSnoozes, setDashTodoSnoozes] = useState({});
  const [remindersCompletedToday, setRemindersCompletedToday] = useState(0);
  const [revisitCount, setRevisitCount] = useState(0);
  const [newDeal, setNewDeal] = useState({
    account_name: '', employee_count: '', stage_id: '', per_employee_rate: '', discount_percent: '0',
    main_contact_name: '', main_contact_email: '', assigned_partner_id: '', assigned_partner_name: '', notes: '', pricing_lines: [], custom_fields: {}
  });
  const [pricingTiers, setPricingTiers] = useState([]);
  const [dealTemplate, setDealTemplate] = useState([]);
  // Pending in-popover spiff quarter reassignments: { [index]: newQuarter }
  const [spiffEdits, setSpiffEdits] = useState({});
  const [savingSpiffs, setSavingSpiffs] = useState(false);

  // Quick action state for dashboard search results
  const [dashOpenPopover, setDashOpenPopover] = useState({ type: null, dealId: null });
  const [dashQuickReminder, setDashQuickReminder] = useState({ dealId: null, note: '', option: '', customDate: '' });
  const [dashQuickTask, setDashQuickTask] = useState({ dealId: null, text: '', section: '' });
  const [dashQuickNote, setDashQuickNote] = useState({ dealId: null, content: '' });
  const [dashTodoSections, setDashTodoSections] = useState([]);
  const [showCardConfig, setShowCardConfig] = useState(false);
  const [cardConfig, setCardConfig] = useState({});
  const [cardConfigDraft, setCardConfigDraft] = useState({});

  // Refresh todo sections when the user ticks something in the floating Quick Notes or main To-Do List
  useEffect(() => {
    // Server-save event: refetch from backend for the source of truth
    const refetch = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/todo-sections`, { withCredentials: true });
        setDashTodoSections(data || []);
      } catch {}
    };
    // Optimistic event: merge the new HTML locally so the Today's Focus tracker
    // updates the instant the user ticks a checkbox (before the debounced save lands).
    const optimistic = (e) => {
      const { section, html } = e.detail || {};
      if (!section) return;
      setDashTodoSections(prev => prev.map(s => s.section === section ? { ...s, content: html } : s));
    };
    window.addEventListener('todos:updated', refetch);
    window.addEventListener('todos:optimistic-update', optimistic);
    return () => {
      window.removeEventListener('todos:updated', refetch);
      window.removeEventListener('todos:optimistic-update', optimistic);
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchQuota = async (fyYear = null) => {
    try {
      const url = fyYear ? `${API_URL}/api/quota?fy=${fyYear}` : `${API_URL}/api/quota`;
      const quotaRes = await axios.get(url, { withCredentials: true });
      setQuota(quotaRes.data);
      if (!selectedFy) setSelectedFy(quotaRes.data.fy_year);
      const qq = quotaRes.data.quarterly_quotas || {};
      const ha = quotaRes.data.historical_attainment || {};
      setQuotaForm(prev => ({
        ...prev,
        q1: (qq.q1 || '').toString(),
        q2: (qq.q2 || '').toString(),
        q3: (qq.q3 || '').toString(),
        q4: (qq.q4 || '').toString(),
        h1: (ha.q1 || '').toString(),
        h2: (ha.q2 || '').toString(),
        h3: (ha.q3 || '').toString(),
        h4: (ha.q4 || '').toString(),
        fiscal_year_start_month: quotaRes.data.fiscal_year_start_month.toString(),
        quarter_months: quotaRes.data.quarter_months.toString(),
        commission_rate: (quotaRes.data.commission_rate || 0).toString(),
        multipliers: quotaRes.data.multipliers || [],
        spiff_bonuses: quotaRes.data.spiff_bonuses || [],
        quarterly_commission_overrides: quotaRes.data.quarterly_commission_overrides || {}
      }));
    } catch {
      console.error('Failed to fetch quota');
    }
  };

  const handleFyChange = (newFy) => {
    setSelectedFy(newFy);
    fetchQuota(newFy);
  };

  const fetchData = async () => {
    try {
      // Use bundle endpoint for most data (1 call instead of 11)
      const [bundleRes, statsRes, quotaRes, onboardRes, templateRes, revisitRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard-bundle`, { withCredentials: true }),
        axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/quota`, { withCredentials: true }),
        axios.get(`${API_URL}/api/onboarding-status`, { withCredentials: true }).catch(() => ({ data: { all_done: true } })),
        axios.get(`${API_URL}/api/deal-template`, { withCredentials: true }).catch(() => ({ data: { fields: [] } })),
        axios.get(`${API_URL}/api/revisit-accounts`, { params: { window_days: 21 }, withCredentials: true }).catch(() => ({ data: { items: [] } })),
      ]);
      setDealTemplate(templateRes.data.fields || []);
      setRevisitCount((revisitRes.data?.items || []).length);
      
      const b = bundleRes.data;
      setStages(b.stages || []);
      setPartners(b.partners || []);
      setQuickLinks(b.quick_links || []);
      setAllAccounts(b.accounts || []);
      setPricingTiers(b.pricing_tiers || []);
      if (b.commission_summary) setCommissionsSummary(b.commission_summary);
      setDashReminders(b.all_reminders || []);
      setRemindersCompletedToday(b.reminders_completed_today || 0);
      setDashTodoSnoozes(b.todo_snoozes || {});
      setActivityFeed([]);
      setTodaysFocus(null);
      setDashboardTodos([]);
      setDashTodoSections(b.todo_sections || []);
      if (!onboardRes.data.all_done) setOnboarding(onboardRes.data);
      
      setStats(statsRes.data);
      setCardConfig(statsRes.data.dashboard_card_config || {});
      setQuota(quotaRes.data);
      setSelectedFy(quotaRes.data.fy_year);
      const qq = quotaRes.data.quarterly_quotas || {};
      const ha = quotaRes.data.historical_attainment || {};
      setQuotaForm({
        q1: (qq.q1 || '').toString(),
        q2: (qq.q2 || '').toString(),
        q3: (qq.q3 || '').toString(),
        q4: (qq.q4 || '').toString(),
        h1: (ha.q1 || '').toString(),
        h2: (ha.q2 || '').toString(),
        h3: (ha.q3 || '').toString(),
        h4: (ha.q4 || '').toString(),
        fiscal_year_start_month: quotaRes.data.fiscal_year_start_month.toString(),
        quarter_months: quotaRes.data.quarter_months.toString(),
        commission_rate: (quotaRes.data.commission_rate || 0).toString(),
        multipliers: quotaRes.data.multipliers || [],
        spiff_bonuses: quotaRes.data.spiff_bonuses || [],
        quarterly_commission_overrides: quotaRes.data.quarterly_commission_overrides || {}
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
      setLoading(false);
    }
  };

  const handleSaveQuota = async () => {
    try {
      await axios.put(`${API_URL}/api/quota`, {
        quarterly_quotas: {
          q1: parseFloat(quotaForm.q1) || 0,
          q2: parseFloat(quotaForm.q2) || 0,
          q3: parseFloat(quotaForm.q3) || 0,
          q4: parseFloat(quotaForm.q4) || 0
        },
        historical_attainment: {
          q1: parseFloat(quotaForm.h1) || 0,
          q2: parseFloat(quotaForm.h2) || 0,
          q3: parseFloat(quotaForm.h3) || 0,
          q4: parseFloat(quotaForm.h4) || 0
        },
        fiscal_year_start_month: parseInt(quotaForm.fiscal_year_start_month),
        quarter_months: parseInt(quotaForm.quarter_months),
        commission_rate: parseFloat(quotaForm.commission_rate) || 0,
        multipliers: quotaForm.multipliers,
        spiff_bonuses: quotaForm.spiff_bonuses,
        quarterly_commission_overrides: quotaForm.quarterly_commission_overrides || {},
        fy_year: selectedFy
      }, { withCredentials: true });
      toast.success('Quota settings saved');
      setShowQuotaSettings(false);
      fetchQuota(selectedFy);
    } catch (error) {
      toast.error('Failed to save quota settings');
    }
  };

  const saveSpiffEdits = async () => {
    if (Object.keys(spiffEdits).length === 0) return;
    setSavingSpiffs(true);
    try {
      const nextSpiffs = (quota.spiff_bonuses || []).map((s, i) => (
        spiffEdits[i] !== undefined ? { ...s, quarter: spiffEdits[i] || undefined } : s
      ));
      await axios.put(`${API_URL}/api/quota`, {
        spiff_bonuses: nextSpiffs,
        fy_year: selectedFy,
      }, { withCredentials: true });
      setSpiffEdits({});
      toast.success('Spiffs reassigned');
      await fetchQuota(selectedFy);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save spiff changes');
    } finally {
      setSavingSpiffs(false);
    }
  };

  const handleCreateDeal = async () => {
    const hasPricingLines = newDeal.pricing_lines && newDeal.pricing_lines.length > 0 && newDeal.pricing_lines.some(l => parseInt(l.quantity) > 0);
    if (!newDeal.account_name || !newDeal.stage_id) {
      toast.error('Please fill in required fields');
      return;
    }
    if (!hasPricingLines && (!newDeal.employee_count || !newDeal.per_employee_rate)) {
      toast.error('Please fill in employee count and rate, or configure pricing tiers');
      return;
    }
    // Validate required custom fields from user's template
    const customErr = validateCustomFields(dealTemplate, newDeal.custom_fields || {});
    if (customErr) { toast.error(customErr); return; }
    try {
      // Auto-create partner if the user typed a new name not in the existing list
      let partnerId = newDeal.assigned_partner_id || null;
      const typedName = (newDeal.assigned_partner_name || '').trim();
      if (!partnerId && typedName) {
        try {
          const { data: newPartner } = await axios.post(`${API_URL}/api/partners`, { company: typedName, name: typedName }, { withCredentials: true });
          partnerId = newPartner.id;
        } catch (err) {
          // Non-fatal — proceed without partner if creation fails
          console.warn('Could not auto-create partner:', err);
        }
      }
      const payload = {
        ...newDeal,
        employee_count: hasPricingLines ? newDeal.pricing_lines.reduce((s, l) => s + (parseInt(l.quantity) || 0), 0) : parseInt(newDeal.employee_count),
        per_employee_rate: parseFloat(newDeal.per_employee_rate) || 0,
        pricing_lines: hasPricingLines ? newDeal.pricing_lines.map(l => ({ ...l, quantity: parseInt(l.quantity) || 0, rate: parseFloat(l.rate) || 0 })) : null,
        discount_percent: parseFloat(newDeal.discount_percent),
        assigned_partner_id: partnerId
      };
      delete payload.assigned_partner_name;
      await axios.post(`${API_URL}/api/accounts`, payload, { withCredentials: true });
      toast.success('Deal created');
      setShowAddDeal(false);
      setNewDeal({ account_name: '', employee_count: '', stage_id: '', per_employee_rate: '', discount_percent: '0', main_contact_name: '', main_contact_email: '', assigned_partner_id: '', assigned_partner_name: '', notes: '', pricing_lines: [], custom_fields: {} });
      fetchData();
    } catch (error) {
      toast.error('Failed to create deal');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/export/accounts`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'accounts_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export completed successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export data');
    }
  };

  const handleFullExport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/export/all`, {
        withCredentials: true,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'crm_full_export.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Full export completed successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Failed to export data');
    }
  };

  const handleSaveLink = async () => {
    if (!linkForm.name.trim() || !linkForm.url.trim()) {
      toast.error('Name and URL are required');
      return;
    }
    let url = linkForm.url.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) url = 'https://' + url;
    try {
      if (editingLink) {
        await axios.put(`${API_URL}/api/quick-links/${editingLink.id}`, { name: linkForm.name.trim(), url }, { withCredentials: true });
        toast.success('Link updated');
      } else {
        await axios.post(`${API_URL}/api/quick-links`, { name: linkForm.name.trim(), url }, { withCredentials: true });
        toast.success('Link added');
      }
      setShowAddLink(false);
      setEditingLink(null);
      setLinkForm({ name: '', url: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to save link');
    }
  };

  const handleDeleteLink = async (linkId) => {
    try {
      await axios.delete(`${API_URL}/api/quick-links/${linkId}`, { withCredentials: true });
      setQuickLinks(prev => prev.filter(l => l.id !== linkId));
      toast.success('Link removed');
    } catch (error) {
      toast.error('Failed to delete link');
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const handleToggleTodo = async (section, index, currentChecked) => {
    // Optimistic update
    setDashboardTodos(prev => prev.map(t =>
      t.section === section && t.index === index ? { ...t, checked: !currentChecked } : t
    ));
    try {
      await axios.put(`${API_URL}/api/dashboard/todo-toggle`, {
        section, index, checked: !currentChecked
      }, { withCredentials: true });
    } catch {
      // Revert on failure
      setDashboardTodos(prev => prev.map(t =>
        t.section === section && t.index === index ? { ...t, checked: currentChecked } : t
      ));
      toast.error('Failed to update task');
    }
  };

  const handlePinnedSave = async (sectionName, html) => {
    try {
      await axios.put(`${API_URL}/api/todo-sections/${encodeURIComponent(sectionName)}`, {
        content: html
      }, { withCredentials: true });
    } catch {
      // silent fail for autosave
    }
  };

  // Quick action handlers for dashboard search
  const handleDashQuickReminder = async (dealId) => {
    const opt = dashQuickReminder.option;
    if (!opt) { toast.error('Select a reminder option'); return; }
    const account = allAccounts.find(a => a.id === dealId);
    const name = account?.account_name || '';
    let dateStr;
    const today = new Date();
    if (opt === 'today') { dateStr = today.toISOString().split('T')[0]; }
    else if (opt === 'tomorrow') { const d = new Date(today); d.setDate(d.getDate() + 1); dateStr = d.toISOString().split('T')[0]; }
    else if (opt === 'next_week') { const d = new Date(today); d.setDate(d.getDate() + 7); dateStr = d.toISOString().split('T')[0]; }
    else if (opt === 'custom') { dateStr = dashQuickReminder.customDate; if (!dateStr) { toast.error('Pick a date'); return; } }
    try {
      await axios.post(`${API_URL}/api/accounts/${dealId}/reminders`, {
        note: dashQuickReminder.note || `Follow up on ${name}`,
        reminder_date: dateStr,
        remind_at: `${dateStr}T05:01:00Z`
      }, { withCredentials: true });
      toast.success('Reminder set!');
      setDashQuickReminder({ dealId: null, note: '', option: '', customDate: '' });
      setDashOpenPopover({ type: null, dealId: null });
    } catch { toast.error('Failed to set reminder'); }
  };

  const handleDashQuickTask = async (dealId) => {
    if (!dashQuickTask.text.trim()) { toast.error('Enter a task'); return; }
    const sectionName = dashQuickTask.section || (dashTodoSections[0]?.section) || 'General';
    const dealName = allAccounts.find(a => a.id === dealId)?.account_name || '';
    const taskText = `${dashQuickTask.text.trim()} [${dealName}]`;
    try {
      let existingContent = '<ul data-type="taskList"></ul>';
      try {
        const fresh = await axios.get(`${API_URL}/api/todo-sections`, { withCredentials: true });
        const sec = fresh.data.find(s => s.section === sectionName);
        if (sec?.content) existingContent = sec.content;
      } catch {}
      const newItem = `<li data-type="taskItem" data-checked="false"><label><input type="checkbox"><span></span></label><div><p>${taskText}</p></div></li>`;
      if (!existingContent.includes('</ul>')) existingContent = `<ul data-type="taskList">${existingContent}</ul>`;
      const updated = existingContent.replace(/(<ul[^>]*data-type="taskList"[^>]*>)/, `$1${newItem}`);
      await axios.put(`${API_URL}/api/todo-sections/${sectionName}`, { content: updated }, { withCredentials: true });
      const refreshed = await axios.get(`${API_URL}/api/todo-sections`, { withCredentials: true }).catch(() => ({ data: dashTodoSections }));
      setDashTodoSections(refreshed.data);
      toast.success(`Task added to "${sectionName}"`);
      setDashQuickTask({ dealId: null, text: '', section: '' });
      setDashOpenPopover({ type: null, dealId: null });
    } catch { toast.error('Failed to add task'); }
  };

  const handleDashQuickNote = async (dealId) => {
    if (!dashQuickNote.content.trim()) { toast.error('Enter a note'); return; }
    try {
      await axios.post(`${API_URL}/api/accounts/${dealId}/feed`, { content: dashQuickNote.content }, { withCredentials: true });
      toast.success('Note added to deal feed!');
      setDashQuickNote({ dealId: null, content: '' });
      setDashOpenPopover({ type: null, dealId: null });
    } catch { toast.error('Failed to add note'); }
  };


  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  };

  const getStageColor = (stageId, index) => {
    const stage = stages.find(s => s.id === stageId);
    return getStageColorByIndex(stage || { id: stageId, name: '' }, index != null ? index : stages.findIndex(s => s.id === stageId));
  };

  const totalAccounts = stats.stats.reduce((sum, s) => sum + s.count, 0);

  if (user?.role === 'manager') return <ManagerDashboard />;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      
      <div className="flex-1 md:ml-64 flex flex-col overflow-hidden">
        <Header />
        
        <div className="flex-1 overflow-auto">
          <div className="p-4 md:p-8">
            {/* Getting Started Checklist */}
            {onboarding && (
              <div className="mb-6 md:mb-8 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      Welcome to deal desk
                    </h2>
                    <p className="text-sm text-slate-500 mt-0.5">Complete these steps to get started</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-indigo-600">{onboarding.completed}/{onboarding.total}</span>
                    <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${(onboarding.completed / onboarding.total) * 100}%` }} />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  {onboarding.steps.map((step) => (
                    <button
                      key={step.key}
                      data-testid={`onboard-${step.key}`}
                      onClick={() => !step.done && navigate(step.link)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                        step.done
                          ? 'bg-emerald-50 border border-emerald-200'
                          : 'bg-slate-50 border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 cursor-pointer'
                      }`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                        step.done ? 'bg-emerald-500' : 'border-2 border-slate-300'
                      }`}>
                        {step.done && <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>}
                      </div>
                      <span className={`text-sm font-medium ${step.done ? 'text-emerald-700 line-through' : 'text-slate-700'}`}>
                        {step.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Welcome + Today's Date */}
            {(() => {
              const now = new Date();
              const hour = now.getHours();
              const greet = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
              const dateStr = now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
              const firstName = (user?.name || '').split(' ')[0] || 'there';
              return (
                <div className="mb-6 flex items-baseline flex-wrap gap-x-3 gap-y-1" data-testid="dashboard-welcome">
                  <h1 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {greet}, {firstName}
                  </h1>
                  <span className="text-slate-400 hidden md:inline">·</span>
                  <p className="text-sm md:text-base text-slate-500 font-semibold">{dateStr}</p>
                </div>
              );
            })()}

            {/* Add New Deal */}
            <div className="mb-6 md:mb-8 flex flex-wrap items-center gap-3">
              <Button
                data-testid="add-new-deal-btn"
                onClick={() => { setNewDeal(d => ({ ...d, stage_id: stages[0]?.id || '', pricing_lines: pricingTiers.map(t => ({ tier_name: t.name, quantity: '', rate: t.default_rate.toString(), rate_type: t.rate_type })) })); setShowAddDeal(true); }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg px-6 py-2.5 shadow-sm hover:shadow-md transition-all font-semibold"
              >
                <Plus size={18} strokeWidth={2} className="mr-2" />
                Add New Deal
              </Button>
              <button
                data-testid="open-tasks-btn"
                onClick={() => navigate('/tasks')}
                className="group flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-indigo-600 bg-white border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition-all shadow-sm"
              >
                <CheckSquare size={16} className="text-indigo-500" />
                Open Tasks
              </button>
            </div>

            {/* Pipeline Snapshot Bar — KPI cards at the top */}
            {stats && (() => {
              const stagesIndex = stats.stats || [];
              const sumArr = (ids) => stagesIndex.filter(s => ids.includes(s.stage_id)).reduce((sum, s) => sum + (s.arr || 0), 0);
              const sumCount = (ids) => stagesIndex.filter(s => ids.includes(s.stage_id)).reduce((sum, s) => sum + (s.count || 0), 0);
              const isClosedStage = (s) => s.stage_name?.toLowerCase().includes('closed');
              const isClosedLost = (s) => s.stage_name?.toLowerCase().includes('closed lost');
              const isClosedWon = (s) => s.stage_name?.toLowerCase().includes('closed won');
              const tpIds = cardConfig.total_pipeline?.length ? cardConfig.total_pipeline : stagesIndex.filter(s => !isClosedLost(s)).map(s => s.stage_id);
              const dipIds = cardConfig.deals_in_play?.length ? cardConfig.deals_in_play : stagesIndex.filter(s => !isClosedStage(s)).map(s => s.stage_id);
              const cwIds  = cardConfig.closed_won?.length ? cardConfig.closed_won : stagesIndex.filter(isClosedWon).map(s => s.stage_id);
              const totalPipelineVal = sumArr(tpIds);
              const dealsInPlayCount = sumCount(dipIds);
              const closedWonVal = cardConfig.closed_won?.length ? sumArr(cwIds) : (stats.current_quarter_closed_won_arr ?? sumArr(cwIds));
              const closedWonLabel = cardConfig.closed_won?.length ? 'Closed Won (all)' : `Closed Won (${quota?.current_quarter_label || 'this quarter'})`;
              return (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="pipeline-snapshot-bar">
                <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                    <BarChart3 size={18} className="text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Total Pipeline</p>
                    <p data-testid="card-total-pipeline" className="text-lg font-black text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {formatCurrency(totalPipelineVal)}
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
                    <Zap size={18} className="text-violet-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Deals in Play</p>
                    <p data-testid="card-deals-in-play" className="text-lg font-black text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {dealsInPlayCount}
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                    <DollarSign size={18} className="text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400" title={closedWonLabel}>{closedWonLabel}</p>
                    <p data-testid="card-closed-won" className="text-lg font-black text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {formatCurrency(closedWonVal)}
                    </p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 relative">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <TrendingUp size={18} className="text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Quota Attainment</p>
                    <p data-testid="card-quota-attainment" className="text-lg font-black text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {Math.round(quota?.quarterly_progress || 0)}%
                    </p>
                  </div>
                  <button
                    data-testid="customize-cards-btn"
                    onClick={() => { setCardConfigDraft({ ...cardConfig }); setShowCardConfig(true); }}
                    className="absolute top-2 right-2 text-slate-300 hover:text-indigo-600 transition-colors"
                    title="Customize which stages count toward each card"
                  >
                    <Sliders size={14} />
                  </button>
                </div>
              </div>
              );
            })()}

            {/* Today's Focus — combined tasks + reminders progress bar */}
            {(() => {
              // Count checked/total task items across included todo sections (excluding snoozed)
              const nowIsoForFocus = new Date().toISOString();
              const snoozesForFocus = dashTodoSnoozes || {};
              let taskTotal = 0, taskChecked = 0;
              const perSection = [];
              (dashTodoSections || []).filter(s => s.include_in_tracker !== false).forEach(sec => {
                const html = sec.content || '';
                const itemRegex = /<li\b([^>]*)>([\s\S]*?)<\/li>/g;
                let m, secTotal = 0, secChecked = 0;
                while ((m = itemRegex.exec(html)) !== null) {
                  const attrs = m[1];
                  if (!/data-type="taskItem"/.test(attrs)) continue;
                  const text = m[2].replace(/<[^>]+>/g, '').trim();
                  const key = `${sec.section}::${text}`;
                  const until = snoozesForFocus[key];
                  if (until && until > nowIsoForFocus) continue; // skip snoozed
                  secTotal++;
                  if (/data-checked="true"/.test(attrs)) secChecked++;
                }
                if (secTotal > 0) {
                  taskTotal += secTotal;
                  taskChecked += secChecked;
                  perSection.push({ name: sec.section, total: secTotal, checked: secChecked });
                }
              });

              // Only count reminders that are DUE today (or overdue)
              const dueReminders = (dashReminders || []).filter(r => r.is_due);
              const activeReminderCount = dueReminders.length;
              // Completed-today reminders contribute to BOTH sides (they were due and now they're done)
              const reminderTotal = activeReminderCount + remindersCompletedToday;
              const total = taskTotal + reminderTotal;
              if (total === 0) return null;
              const checked = taskChecked + remindersCompletedToday;
              const pct = Math.round((checked / total) * 100);
              const remaining = total - checked;
              const complete = remaining === 0;
              const dueNow = activeReminderCount;

              return (
                <button
                  onClick={() => navigate('/tasks')}
                  data-testid="dashboard-todo-progress"
                  className="w-full text-left rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 hover:from-slate-800 hover:via-slate-700 hover:to-slate-800 shadow-lg shadow-slate-900/20 transition-all overflow-hidden mb-4 group"
                >
                  <div className="px-6 py-5">
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <ListChecks size={16} className="text-indigo-400 flex-shrink-0" />
                        <p className="text-xs font-bold uppercase tracking-wider text-indigo-300">Today&apos;s Focus</p>
                        {complete && <span className="text-[10px] font-bold text-emerald-300 bg-emerald-900/40 px-2 py-0.5 rounded-full">All done!</span>}
                        {!complete && dueNow > 0 && (
                          <span className="text-[10px] font-bold text-red-300 bg-red-900/40 border border-red-800 px-2 py-0.5 rounded-full">
                            {dueNow} due now
                          </span>
                        )}
                      </div>
                      <div className="flex items-baseline gap-1 flex-shrink-0">
                        <span className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{checked}</span>
                        <span className="text-sm text-slate-400">/ {total} done</span>
                      </div>
                    </div>
                    <div className="w-full h-4 bg-slate-700/60 rounded-full overflow-hidden">
                      <div
                        data-testid="todo-progress-bar"
                        className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-700 ease-out shadow-lg shadow-indigo-500/30"
                        style={{ width: `${Math.max(pct, 2)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm font-bold text-indigo-300">
                        {pct}% complete
                      </span>
                      <span className="text-sm text-slate-400">
                        {complete ? "You're crushing it" : `${remaining} item${remaining === 1 ? '' : 's'} left`}
                      </span>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-700/60 flex items-center gap-4 flex-wrap text-[11px]">
                      {taskTotal > 0 && (
                        <span className="flex items-center gap-1.5 text-slate-400">
                          <CheckSquare size={12} className="text-indigo-400" />
                          <span className="font-bold text-slate-200">{taskChecked}/{taskTotal}</span> tasks
                        </span>
                      )}
                      {reminderTotal > 0 && (
                        <span className="flex items-center gap-1.5 text-slate-400">
                          <Bell size={12} className="text-red-400" />
                          <span className="font-bold text-slate-200">{remindersCompletedToday}/{reminderTotal}</span> reminder{reminderTotal === 1 ? '' : 's'} done
                        </span>
                      )}
                      {perSection.slice(0, 3).map(s => (
                        <span key={s.name} className="text-slate-500">·
                          <span className="ml-1 font-bold text-slate-300">{s.checked}/{s.total}</span> <span className="text-slate-500">{s.name}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </button>
              );
            })()}

            {/* Revisit pill — accounts to re-engage from last year at this time */}
            {revisitCount > 0 && (
              <button
                data-testid="dashboard-revisit-pill"
                onClick={() => navigate('/revisit')}
                className="w-full text-left bg-white hover:bg-slate-50 rounded-2xl border border-slate-200 hover:border-indigo-300 shadow-sm hover:shadow transition-all mb-4 px-5 py-3 flex items-center gap-3 group"
              >
                <span className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center flex-shrink-0">
                  <RotateCcw size={16} className="text-indigo-600" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-900">
                    <span className="text-indigo-700">{revisitCount}</span> account{revisitCount === 1 ? '' : 's'} to revisit
                  </p>
                  <p className="text-[11px] text-slate-500">Last year at this time — a nudge to re-engage</p>
                </div>
                <ChevronRight size={16} className="text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition-all" />
              </button>
            )}

            {/* Tasks (Follow-ups) strip — reminders due today/overdue + unchecked to-dos */}
            {(() => {
              const today = startOfDay(new Date());
              const dueReminders = (dashReminders || [])
                .filter(r => !r.dismissed && r.remind_at)
                .filter(r => {
                  const d = startOfDay(new Date(r.remind_at));
                  return d <= today;
                })
                .sort((a, b) => new Date(a.remind_at) - new Date(b.remind_at))
                .map(r => ({ kind: 'reminder', id: r.id, data: r }));

              // Extract unchecked task items from included todo sections
              const uncheckedTodos = [];
              (dashTodoSections || [])
                .filter(s => s.include_in_tracker !== false)
                .forEach(sec => {
                  const html = sec.content || '';
                  const itemRegex = /<li\b([^>]*)>([\s\S]*?)<\/li>/g;
                  let m; let idx = 0;
                  while ((m = itemRegex.exec(html)) !== null) {
                    const attrs = m[1];
                    if (!/data-type="taskItem"/.test(attrs)) continue;
                    const isChecked = /data-checked="true"/.test(attrs);
                    if (!isChecked) {
                      const text = m[2].replace(/<[^>]+>/g, '').trim();
                      if (text) uncheckedTodos.push({ kind: 'todo', id: `todo-${sec.section}-${idx}`, section: sec.section, index: idx, text });
                    }
                    idx++;
                  }
                });

              // Show ALL due reminders + ALL unchecked to-dos (filtered by snooze), scrollable
              const nowIso = new Date().toISOString();
              const todoSnoozes = dashTodoSnoozes || {};
              const visibleTodos = uncheckedTodos.filter(t => {
                const key = `${t.section}::${t.text}`;
                const until = todoSnoozes[key];
                return !until || until <= nowIso;
              });
              const snoozedTodoCount = uncheckedTodos.length - visibleTodos.length;
              const followups = [
                ...dueReminders,
                ...visibleTodos.map(t => ({ kind: 'todo', id: t.id, data: t })),
              ];
              const overdueCount = dueReminders.filter(x => x.data.is_due).length;
              const totalCount = dueReminders.length + visibleTodos.length;

              const refreshBadges = () => window.dispatchEvent(new CustomEvent('sidebar-badges:refresh'));

              const doSnooze = async (id, days) => {
                try {
                  const r = dashReminders.find(x => x.id === id);
                  const next = new Date();
                  next.setDate(next.getDate() + days);
                  if (r?.remind_at) {
                    const prev = new Date(r.remind_at);
                    next.setHours(prev.getHours(), prev.getMinutes(), 0, 0);
                  } else {
                    next.setHours(9, 0, 0, 0);
                  }
                  await axios.put(`${API_URL}/api/deal-reminders/${id}`, { remind_at: next.toISOString() }, { withCredentials: true });
                  setDashReminders(prev => prev.filter(x => x.id !== id));
                  refreshBadges();
                  toast.success(`Rescheduled ${days === 1 ? 'to tomorrow' : `+${days}d`}`);
                } catch { toast.error('Failed'); }
              };
              const doComplete = async (id) => {
                try {
                  await axios.put(`${API_URL}/api/deal-reminders/${id}/dismiss`, {}, { withCredentials: true });
                  const wasDue = !!dashReminders.find(x => x.id === id)?.is_due;
                  setDashReminders(prev => prev.filter(x => x.id !== id));
                  if (wasDue) setRemindersCompletedToday(prev => prev + 1);
                  refreshBadges();
                  toast.success('Reminder completed');
                } catch { toast.error('Failed'); }
              };
              const doTodoSnooze = async (section, text, days = 1) => {
                const key = `${section}::${text}`;
                const until = new Date();
                until.setDate(until.getDate() + days);
                until.setHours(9, 0, 0, 0);
                setDashTodoSnoozes(prev => ({ ...prev, [key]: until.toISOString() }));
                try {
                  await axios.post(`${API_URL}/api/todo-snooze`, { section, text, days }, { withCredentials: true });
                  refreshBadges();
                  const label = days === 1 ? 'tomorrow' : days === 30 ? '1 month' : days === 180 ? '6 months' : days === 365 ? '1 year' : `${days} days`;
                  toast.success(`Snoozed for ${label}`, {
                    description: 'View snoozed to-dos in the Boards tab (💤 pill)',
                    action: { label: 'View', onClick: () => navigate('/tasks?tab=boards') },
                  });
                } catch { toast.error('Failed'); }
              };
              const doTodoDone = async (section, index) => {
                // Optimistic remove from local sections HTML
                setDashTodoSections(prev => prev.map(s => {
                  if (s.section !== section) return s;
                  const html = s.content || '';
                  const itemRegex = /<li\b([^>]*)>([\s\S]*?)<\/li>/g;
                  let count = -1;
                  const next = html.replace(itemRegex, (match, attrs) => {
                    if (!/data-type="taskItem"/.test(attrs)) return match;
                    count += 1;
                    if (count === index) {
                      return match.replace(/data-checked="false"/, 'data-checked="true"');
                    }
                    return match;
                  });
                  return { ...s, content: next };
                }));
                try {
                  await axios.put(`${API_URL}/api/dashboard/todo-toggle`, { section, index, checked: true }, { withCredentials: true });
                  toast.success('Task completed');
                } catch {
                  toast.error('Failed');
                }
              };

              return (
                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm mb-6 overflow-hidden" data-testid="dashboard-followups-strip">
                  <div className="px-5 py-3 flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-indigo-50 via-violet-50 to-white">
                    <div className="flex items-center gap-2">
                      <Bell size={16} className="text-indigo-600" />
                      <p className="text-sm font-black tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Tasks</p>
                      <span className="text-[10px] font-bold bg-indigo-600 text-white px-2 py-0.5 rounded-full">{totalCount}</span>
                      {overdueCount > 0 && (
                        <span className="text-[10px] font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">{overdueCount} overdue</span>
                      )}
                    </div>
                    <button
                      onClick={() => navigate('/tasks')}
                      data-testid="dashboard-see-all-tasks-btn"
                      className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 flex items-center gap-1"
                    >
                      See all in Tasks <ChevronRight size={12} />
                    </button>
                  </div>
                  <div className="max-h-[420px] overflow-y-auto px-5 divide-y divide-slate-100">
                    {followups.length === 0 ? (
                      <div className="py-8 text-center">
                        <CheckSquare size={22} className="text-emerald-500 mx-auto mb-1" />
                        <p className="text-sm text-slate-600">You&apos;re caught up.</p>
                      </div>
                    ) : (
                      followups.map(item => {
                        if (item.kind === 'reminder') {
                          const r = item.data;
                          const dueDate = new Date(r.remind_at);
                          const overdueDays = Math.max(0, Math.floor((today - startOfDay(dueDate)) / 86400000));
                          return (
                            <div key={r.id} className="flex items-center gap-3 py-2.5" data-testid={`dashboard-followup-${r.id}`}>
                              <div className={`w-1 self-stretch rounded-full ${overdueDays > 0 ? 'bg-red-400' : 'bg-emerald-400'}`} />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className="text-[9px] font-bold bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded flex items-center gap-0.5 flex-shrink-0">
                                    <Bell size={8} /> Reminder
                                  </span>
                                  <p className="text-sm font-semibold text-slate-800 truncate">{r.note || 'Reminder'}</p>
                                  {r.recurrence && (
                                    <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                                      <Repeat size={8} />{r.recurrence}
                                    </span>
                                  )}
                                  {overdueDays > 0 && (
                                    <span className="text-[9px] font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded">
                                      {overdueDays}d late
                                    </span>
                                  )}
                                </div>
                                {r.account_name && (
                                  <button
                                    onClick={() => r.account_id && navigate(`/account/${r.account_id}`)}
                                    className="mt-0.5 text-xs text-slate-500 hover:text-indigo-600 flex items-center gap-0.5"
                                    data-testid={`dashboard-followup-account-${r.id}`}
                                  >
                                    {r.account_name} <ArrowUpRight size={9} />
                                  </button>
                                )}
                              </div>
                              {/* Desktop actions */}
                              <div className="hidden md:flex items-center gap-1 flex-shrink-0">
                                <button
                                  onClick={() => doSnooze(r.id, 1)}
                                  data-testid={`dashboard-snooze-${r.id}-1d`}
                                  className="px-2 py-1 text-[11px] font-semibold text-slate-600 bg-white hover:bg-indigo-50 hover:text-indigo-700 border border-slate-200 rounded-md transition-colors"
                                  title="Tomorrow"
                                >Tomorrow</button>
                                <button
                                  onClick={() => doSnooze(r.id, 3)}
                                  data-testid={`dashboard-snooze-${r.id}-3d`}
                                  className="px-2 py-1 text-[11px] font-semibold text-slate-600 bg-white hover:bg-indigo-50 hover:text-indigo-700 border border-slate-200 rounded-md transition-colors"
                                  title="+3 days"
                                >+3d</button>
                                <button
                                  onClick={() => doSnooze(r.id, 7)}
                                  data-testid={`dashboard-snooze-${r.id}-7d`}
                                  className="px-2 py-1 text-[11px] font-semibold text-slate-600 bg-white hover:bg-indigo-50 hover:text-indigo-700 border border-slate-200 rounded-md transition-colors"
                                  title="Next week"
                                >Next week</button>
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <button
                                      data-testid={`dashboard-snooze-more-${r.id}`}
                                      className="px-2 py-1 text-[11px] font-semibold text-slate-600 bg-white hover:bg-indigo-50 hover:text-indigo-700 border border-slate-200 rounded-md transition-colors"
                                      title="More snooze options"
                                    >Later ▾</button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-40 p-1 rounded-lg" align="end">
                                    <button onClick={() => doSnooze(r.id, 30)} data-testid={`dashboard-snooze-${r.id}-30d`} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">+1 month</button>
                                    <button onClick={() => doSnooze(r.id, 90)} data-testid={`dashboard-snooze-${r.id}-90d`} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">+3 months</button>
                                    <button onClick={() => doSnooze(r.id, 180)} data-testid={`dashboard-snooze-${r.id}-180d`} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">+6 months</button>
                                    <button onClick={() => doSnooze(r.id, 365)} data-testid={`dashboard-snooze-${r.id}-365d`} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">+1 year</button>
                                  </PopoverContent>
                                </Popover>
                                <button
                                  onClick={() => doComplete(r.id)}
                                  data-testid={`dashboard-complete-${r.id}`}
                                  className="px-2 py-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-md flex items-center gap-1 transition-colors"
                                  title="Mark completed"
                                >
                                  <Check size={11} /> Done
                                </button>
                              </div>
                              {/* Mobile overflow */}
                              <div className="md:hidden flex-shrink-0">
                                <Popover>
                                  <PopoverTrigger asChild>
                                    <button
                                      data-testid={`dashboard-followup-mobile-actions-${r.id}`}
                                      className="p-1.5 rounded-md text-slate-500 hover:text-indigo-700 hover:bg-indigo-50 border border-slate-200"
                                      title="Actions"
                                    ><Clock size={14} /></button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-40 p-1 rounded-lg" align="end">
                                    <button data-testid={`dashboard-mobile-snooze-${r.id}-1d`} onClick={() => doSnooze(r.id, 1)} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">Tomorrow</button>
                                    <button data-testid={`dashboard-mobile-snooze-${r.id}-3d`} onClick={() => doSnooze(r.id, 3)} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">+3 days</button>
                                    <button data-testid={`dashboard-mobile-snooze-${r.id}-7d`} onClick={() => doSnooze(r.id, 7)} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">Next week</button>
                                    <button data-testid={`dashboard-mobile-snooze-${r.id}-30d`} onClick={() => doSnooze(r.id, 30)} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">+1 month</button>
                                    <button data-testid={`dashboard-mobile-snooze-${r.id}-90d`} onClick={() => doSnooze(r.id, 90)} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">+3 months</button>
                                    <button data-testid={`dashboard-mobile-snooze-${r.id}-180d`} onClick={() => doSnooze(r.id, 180)} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">+6 months</button>
                                    <button data-testid={`dashboard-mobile-snooze-${r.id}-365d`} onClick={() => doSnooze(r.id, 365)} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">+1 year</button>
                                    <button data-testid={`dashboard-mobile-complete-${r.id}`} onClick={() => doComplete(r.id)} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-emerald-50 text-emerald-700 flex items-center gap-1"><Check size={11} /> Done</button>
                                  </PopoverContent>
                                </Popover>
                              </div>
                            </div>
                          );
                        }
                        // TODO row
                        const t = item.data;
                        return (
                          <div key={item.id} className="flex items-center gap-3 py-2.5" data-testid={`dashboard-followup-todo-${t.section}-${t.index}`}>
                            <div className="w-1 self-stretch rounded-full bg-sky-400" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-[9px] font-bold bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded flex items-center gap-0.5 flex-shrink-0">
                                  <ListChecks size={8} /> To-Do
                                </span>
                                <p className="text-sm font-semibold text-slate-800 truncate">{t.text}</p>
                              </div>
                              <p className="mt-0.5 text-xs text-slate-500 truncate">in {t.section}</p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button
                                    data-testid={`dashboard-todo-snooze-${t.section}-${t.index}`}
                                    className="px-2 py-1 text-[11px] font-semibold text-slate-600 bg-white hover:bg-indigo-50 hover:text-indigo-700 border border-slate-200 rounded-md transition-colors"
                                    title="Snooze"
                                  >
                                    💤 Snooze ▾
                                  </button>
                                </PopoverTrigger>
                                <PopoverContent className="w-40 p-1 rounded-lg" align="end">
                                  <button onClick={() => doTodoSnooze(t.section, t.text, 1)} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">Tomorrow</button>
                                  <button onClick={() => doTodoSnooze(t.section, t.text, 7)} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">Next week</button>
                                  <button onClick={() => doTodoSnooze(t.section, t.text, 30)} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">+1 month</button>
                                  <button onClick={() => doTodoSnooze(t.section, t.text, 180)} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">+6 months</button>
                                  <button onClick={() => doTodoSnooze(t.section, t.text, 365)} className="w-full text-left px-2 py-1.5 text-xs font-semibold rounded hover:bg-indigo-50 hover:text-indigo-700">+1 year</button>
                                </PopoverContent>
                              </Popover>
                              <button
                                onClick={() => doTodoDone(t.section, t.index)}
                                data-testid={`dashboard-todo-done-${t.section}-${t.index}`}
                                className="px-2 py-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-md flex items-center gap-1 transition-colors"
                                title="Mark completed"
                              >
                                <Check size={11} /> Done
                              </button>
                            </div>
                          </div>
                        );
                      })
                    )}
                    {snoozedTodoCount > 0 && (
                      <div className="py-2 text-center">
                        <button
                          onClick={() => navigate('/tasks?tab=timeline')}
                          data-testid="dashboard-snoozed-count-link"
                          className="text-[11px] font-semibold text-slate-500 hover:text-indigo-700 inline-flex items-center gap-1"
                        >
                          <Clock size={11} /> {snoozedTodoCount} to-do{snoozedTodoCount === 1 ? '' : 's'} snoozed — view in Tasks
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}


            {/* Quick Links */}
            <div className="mb-8">
              <div className="rounded-2xl border border-indigo-200/60 shadow-sm overflow-hidden" data-testid="quick-links-panel" style={{ background: 'linear-gradient(135deg, #eef2ff 0%, #ffffff 60%)' }}>
                <div className="px-5 py-3.5 flex items-center justify-between border-b border-indigo-100/60">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                      <Link2 size={16} className="text-indigo-600" />
                    </div>
                    <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Quick Links</h3>
                  </div>
                  <button
                    data-testid="add-quick-link-btn"
                    onClick={() => { setEditingLink(null); setLinkForm({ name: '', url: '' }); setShowAddLink(true); }}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-indigo-500 text-white hover:bg-indigo-600 transition-colors shadow-sm"
                  >
                    <Plus size={14} />
                  </button>
                </div>
                <div className="p-3">
                  {quickLinks.length === 0 ? (
                    <div className="text-center py-5">
                      <Link2 size={24} className="mx-auto mb-2 text-indigo-200" />
                      <p className="text-xs text-slate-400">Add your calendar, Zoom, or CRM links</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {quickLinks.map(link => (
                        <div key={link.id} data-testid={`quick-link-${link.id}`} className="group flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white border border-slate-100 shadow-sm hover:border-indigo-200 hover:shadow-md transition-all hover:scale-[1.01]">
                          <a href={link.url} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center gap-2.5 min-w-0">
                            <div className="w-6 h-6 rounded-md bg-indigo-50 flex items-center justify-center flex-shrink-0">
                              <ExternalLink size={12} className="text-indigo-500" />
                            </div>
                            <span className="text-sm font-medium text-slate-700 truncate">{link.name}</span>
                          </a>
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => copyToClipboard(link.url)} className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600" title="Copy link">
                              <Copy size={12} />
                            </button>
                            <button onClick={() => { setEditingLink(link); setLinkForm({ name: link.name, url: link.url }); setShowAddLink(true); }} className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-slate-600" title="Edit">
                              <Pencil size={12} />
                            </button>
                            <button onClick={() => handleDeleteLink(link.id)} className="p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-600" title="Delete">
                              <Trash size={12} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Quota Tracking */}
            {quota && (
              <>
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 md:p-8 mb-8">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                      <Target size={20} strokeWidth={2.5} className="text-indigo-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Quota Tracker
                      </h2>
                      <p className="text-sm text-slate-500">{quota.current_quarter_label}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* FY Navigator */}
                    {quota.available_fys && quota.available_fys.length > 0 && (
                      <div className="flex items-center gap-1 bg-slate-50 rounded-lg border border-slate-200 px-1" data-testid="fy-navigator">
                        <button
                          data-testid="fy-prev-btn"
                          onClick={() => {
                            const fys = quota.available_fys;
                            const idx = fys.findIndex(f => f.year === selectedFy);
                            if (idx > 0) handleFyChange(fys[idx - 1].year);
                          }}
                          disabled={quota.available_fys.findIndex(f => f.year === selectedFy) === 0}
                          className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft size={16} />
                        </button>
                        <span className={`px-3 py-1.5 text-sm font-bold tracking-wide ${quota.is_current_fy ? 'text-indigo-600' : 'text-slate-600'}`} data-testid="fy-label">
                          {quota.fy_label}
                        </span>
                        <button
                          data-testid="fy-next-btn"
                          onClick={() => {
                            const fys = quota.available_fys;
                            const idx = fys.findIndex(f => f.year === selectedFy);
                            if (idx < fys.length - 1) handleFyChange(fys[idx + 1].year);
                          }}
                          disabled={quota.available_fys.findIndex(f => f.year === selectedFy) === quota.available_fys.length - 1}
                          className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronRight size={16} />
                        </button>
                      </div>
                    )}
                    {!quota.is_current_fy && (
                      <button
                        onClick={() => handleFyChange(quota.available_fys?.find(f => f.is_current)?.year)}
                        className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-md transition-colors"
                        data-testid="fy-current-btn"
                      >
                        Current
                      </button>
                    )}
                    <button
                      data-testid="quota-settings-btn"
                      onClick={() => setShowQuotaSettings(true)}
                      className="flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700 transition-colors"
                    >
                      <Settings size={16} />
                      Configure
                    </button>
                  </div>
                </div>

                {quota.annual_quota > 0 ? (
                  <div className="space-y-6">
                    {/* Current Quarter with Gauge */}
                    <div className="flex flex-col md:flex-row gap-6 items-center">
                      {/* Gauge Meter */}
                      <div className="flex-shrink-0" data-testid="quota-gauge-meter">
                        <div className="relative w-48 h-32">
                          <svg viewBox="0 0 200 120" className="w-full h-full">
                            {/* Background arc */}
                            <path d="M 20 110 A 80 80 0 0 1 180 110" fill="none" stroke="#e2e8f0" strokeWidth="16" strokeLinecap="round" />
                            {/* Progress arc */}
                            <path d="M 20 110 A 80 80 0 0 1 180 110" fill="none"
                              stroke={
                                quota.quarterly_progress >= 100 ? '#059669' :
                                quota.quarterly_progress >= 75 ? '#4f46e5' :
                                quota.quarterly_progress >= 50 ? '#7c3aed' : '#f59e0b'
                              }
                              strokeWidth="16" strokeLinecap="round"
                              strokeDasharray={`${Math.min(quota.quarterly_progress, 120) / 120 * 251.2} 251.2`}
                              style={{ transition: 'stroke-dasharray 1s ease-out' }}
                            />
                          </svg>
                          {/* Overlay text */}
                          <div className="absolute inset-0 flex flex-col items-center justify-end pb-4">
                            <span className="text-3xl font-black" style={{
                              fontFamily: 'Outfit, sans-serif',
                              color: quota.quarterly_progress >= 100 ? '#059669' : quota.quarterly_progress >= 75 ? '#4f46e5' : '#1e293b'
                            }}>
                              {Math.round(quota.quarterly_progress)}%
                            </span>
                            <span className="text-[10px] text-slate-400 font-medium">of quarterly quota</span>
                          </div>
                        </div>
                      </div>

                      {/* Quarter Details */}
                      <div className="flex-1 w-full">
                        <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-3 gap-3">
                          <div>
                            <p className="text-xs font-bold uppercase tracking-wider text-indigo-500 mb-1">{quota.current_quarter_label}</p>
                            <p className="text-3xl md:text-4xl font-black text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                              {formatCurrency(quota.quarterly_closed_won)}
                            </p>
                          </div>
                          <div className="text-left sm:text-right">
                            <p className="text-sm text-slate-500 mb-1">Quarter Target</p>
                            <p className="text-2xl font-bold text-slate-600" style={{ fontFamily: 'Outfit, sans-serif' }}>
                              {formatCurrency(quota.current_quarter_quota)}
                            </p>
                          </div>
                        </div>
                        <div className="relative">
                          <div className="w-full h-5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              data-testid="quota-progress-bar-quarterly"
                              className={`h-full rounded-full transition-all duration-700 ease-out ${
                                quota.quarterly_progress >= 100 ? 'bg-emerald-500' :
                                quota.quarterly_progress >= 75 ? 'bg-indigo-500' :
                                quota.quarterly_progress >= 50 ? 'bg-violet-500' :
                                'bg-amber-500'
                              }`}
                              style={{ width: `${Math.min(quota.quarterly_progress, 100)}%` }}
                            />
                          </div>
                          <div className="flex items-center justify-between mt-2">
                            <span className={`text-sm font-bold ${
                              quota.quarterly_progress >= 100 ? 'text-emerald-600' :
                              quota.quarterly_progress >= 75 ? 'text-indigo-600' :
                              'text-slate-600'
                            }`}>
                              {Math.round(quota.quarterly_progress)}% of quarterly quota
                            </span>
                            <span className="text-sm text-slate-500">
                              {quota.quarterly_progress >= 100
                                ? `${formatCurrency(quota.quarterly_closed_won - quota.current_quarter_quota)} over target`
                                : `${formatCurrency(quota.current_quarter_quota - quota.quarterly_closed_won)} remaining`
                              }
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Estimated Commission - Under Quarterly */}
                    {quota.commission_rate > 0 && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            data-testid="commission-breakdown-btn"
                            className="block w-full text-left p-4 bg-emerald-50 rounded-xl border border-emerald-200 hover:bg-emerald-100/60 hover:border-emerald-300 transition-colors cursor-pointer"
                          >
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1">
                                Estimated Commission
                                <span className="text-[10px] text-emerald-500 font-semibold normal-case">· tap to see breakdown</span>
                              </p>
                              <p className="text-xl font-bold text-emerald-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatCurrency(quota.estimated_commission)}</p>
                            </div>
                            <p className="text-xs text-emerald-600 mt-1">
                              {quota.current_quarter_label || 'This quarter'}
                              {' · '}
                              {quota.commission_rate}% base rate
                              {quota.multipliers?.length > 0 && ` · ${quota.multipliers.length} multiplier(s)`}
                              {(() => {
                                const cqShort = (quota.all_quarters || []).find(q => q.is_current)?.short_label;
                                const matching = (quota.spiff_bonuses || []).filter(b => !b.quarter || b.quarter === cqShort || b.quarter === quota.current_quarter_label);
                                return matching.length > 0
                                  ? ` · ${matching.reduce((s, b) => s + b.amount, 0).toLocaleString('en-US', {style: 'currency', currency: 'USD'})} in spiffs`
                                  : '';
                              })()}
                            </p>
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          data-testid="commission-breakdown-popover"
                          className="w-80 p-4 rounded-xl border border-emerald-200 shadow-lg"
                          align="end"
                        >
                          {(() => {
                            const b = quota.commission_breakdown || {};
                            const fmt = (n) => (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
                            const lines = [];
                            if ((b.base || 0) > 0) lines.push({ label: `Base commission (${b.quarter_label || ''})`, value: b.base, color: 'text-slate-700' });
                            if ((b.boost || 0) > 0) lines.push({ label: 'Multiplier boost 🚀', value: b.boost, color: 'text-violet-700', hint: 'From per-deal slices that pushed cumulative attainment past a multiplier tier' });
                            if ((b.manual || 0) > 0) lines.push({ label: 'Manual $ overrides', value: b.manual, color: 'text-amber-700' });
                            if ((b.spiffs || 0) > 0) lines.push({ label: `Spiffs (${(b.spiff_items || []).length})`, value: b.spiffs, color: 'text-pink-700' });
                            return (
                              <div>
                                <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">How this number is built</p>
                                <div className="space-y-2">
                                  {lines.length === 0 && (
                                    <p className="text-sm text-slate-500 italic">No closed-won deals or spiffs in {b.quarter_label || 'this quarter'} yet.</p>
                                  )}
                                  {lines.map((l, i) => (
                                    <div key={i} className="flex items-start justify-between gap-3" data-testid={`breakdown-line-${i}`}>
                                      <div className="min-w-0 flex-1">
                                        <p className={`text-sm font-semibold ${l.color}`}>{l.label}</p>
                                        {l.hint && <p className="text-[10px] text-slate-400 mt-0.5 leading-tight">{l.hint}</p>}
                                      </div>
                                      <p className={`text-sm font-bold ${l.color} flex-shrink-0`}>{fmt(l.value)}</p>
                                    </div>
                                  ))}
                                  {(b.spiff_items || []).length > 0 && (
                                    <div className="pl-2 mt-1 border-l-2 border-pink-100 space-y-0.5">
                                      {b.spiff_items.map((s, i) => (
                                        <div key={i} className="flex justify-between text-[11px] text-pink-600/80">
                                          <span className="truncate pr-2">↳ {s.name}</span>
                                          <span className="flex-shrink-0">{fmt(s.amount)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {lines.length > 0 && (
                                  <div className="border-t border-emerald-200 mt-3 pt-3 flex items-center justify-between">
                                    <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Total</p>
                                    <p className="text-lg font-black text-emerald-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{fmt(b.total)}</p>
                                  </div>
                                )}

                                {/* All spiffs — across every quarter — with one-click reassign */}
                                {(quota.spiff_bonuses || []).length > 0 && (
                                  <div className="border-t border-slate-200 mt-3 pt-3" data-testid="all-spiffs-panel">
                                    <div className="flex items-center justify-between mb-2">
                                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">All configured spiffs ({(quota.spiff_bonuses || []).length})</p>
                                      {Object.keys(spiffEdits).length > 0 && (
                                        <div className="flex gap-1.5">
                                          <button
                                            onClick={() => setSpiffEdits({})}
                                            className="text-xs font-semibold text-slate-500 hover:text-slate-700 px-2 py-0.5"
                                            data-testid="spiff-reassign-cancel"
                                          >
                                            Cancel
                                          </button>
                                          <button
                                            onClick={saveSpiffEdits}
                                            disabled={savingSpiffs}
                                            className="text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 px-2.5 py-0.5 rounded-md"
                                            data-testid="spiff-reassign-save"
                                          >
                                            {savingSpiffs ? 'Saving…' : 'Save'}
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                    <div className="space-y-1.5 max-h-52 overflow-y-auto pr-1">
                                      {(quota.spiff_bonuses || []).map((s, idx) => {
                                        const cqShort = (quota.all_quarters || []).find(q => q.is_current)?.short_label;
                                        const pendingQ = spiffEdits[idx];
                                        const displayQ = pendingQ !== undefined ? pendingQ : (s.quarter || '');
                                        const applied = !displayQ || displayQ === cqShort || displayQ === quota.current_quarter_label;
                                        const edited = pendingQ !== undefined && pendingQ !== (s.quarter || '');
                                        return (
                                          <div key={idx} className={`flex items-center gap-2 rounded-md px-2 py-1.5 border ${edited ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-100 bg-slate-50/50'}`} data-testid={`spiff-row-${idx}`}>
                                            <div className="flex-1 min-w-0">
                                              <div className="flex items-center gap-1.5 min-w-0">
                                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${applied ? 'bg-emerald-500' : 'bg-slate-300'}`} title={applied ? 'Applied to current quarter' : 'Assigned to a different quarter'} />
                                                <p className="text-xs font-semibold text-slate-700 truncate">{s.name || 'Spiff'}</p>
                                              </div>
                                              <p className="text-[10px] text-slate-500 mt-0.5">{fmt(s.amount || 0)}</p>
                                            </div>
                                            <select
                                              value={displayQ}
                                              onChange={(e) => setSpiffEdits({ ...spiffEdits, [idx]: e.target.value })}
                                              className="text-xs rounded border border-slate-200 bg-white px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-400"
                                              data-testid={`spiff-quarter-${idx}`}
                                            >
                                              <option value="">Unassigned (always applies)</option>
                                              {(quota.all_quarters || []).map((q) => (
                                                <option key={q.short_label} value={q.short_label}>
                                                  {q.short_label}{q.is_current ? ' (current)' : ''}
                                                </option>
                                              ))}
                                              {/* If the persisted quarter is not in the visible FY's quarters, surface it explicitly so the control shows the true value */}
                                              {displayQ && !(quota.all_quarters || []).some((q) => q.short_label === displayQ) && (
                                                <option key={`legacy-${displayQ}`} value={displayQ}>{displayQ} (other FY)</option>
                                              )}
                                            </select>
                                          </div>
                                        );
                                      })}
                                    </div>
                                    <p className="text-xs text-slate-500 mt-2 leading-tight">
                                      Only spiffs marked ● contribute to the current quarter&apos;s commission. Change the quarter to reassign — no need to leave this popover.
                                    </p>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </PopoverContent>
                      </Popover>
                    )}

                    {/* Quota Simulator - Inline */}
                    {quota.annual_quota > 0 && (
                      <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="quota-simulator">
                        <button
                          data-testid="simulator-toggle"
                          onClick={() => setShowSimulator(!showSimulator)}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                              <Zap size={16} strokeWidth={2.5} className="text-violet-600" />
                            </div>
                            <div className="text-left">
                              <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Quota Simulator</h3>
                              <p className="text-[10px] text-slate-500">What-if: see your path(s) to quota</p>
                            </div>
                          </div>
                          <ChevronRight size={16} className={`text-slate-400 transition-transform ${showSimulator ? 'rotate-90' : ''}`} />
                        </button>
                        
                        {showSimulator && (() => {
                          const currentQ = quota.all_quarters?.find(q => q.is_current) || quota.all_quarters?.[0];
                          const currentQQuota = currentQ?.quota || 0;
                          const currentQClosed = currentQ?.closed_won || 0;
                          const simTotal = simDeals.reduce((sum, d) => sum + (parseFloat(d.arr) || 0), 0);
                          const projectedQClosed = currentQClosed + simTotal;
                          const projectedQProgress = currentQQuota > 0 ? Math.round((projectedQClosed / currentQQuota) * 100) : 0;
                          const projectedAnnual = quota.annual_closed_won + simTotal;
                          const projectedAnnualProgress = quota.annual_quota > 0 ? Math.round((projectedAnnual / quota.annual_quota) * 100) : 0;
                          const gapToQuarterQuota = Math.max(0, currentQQuota - currentQClosed);
                          // quota.commission_rate and quota.multipliers are already the resolved current-quarter values
                          // (backend applies per-quarter overrides in _resolve_quarter_commission_config).
                          const commRate = quota.commission_rate || 0;
                          const multipliers = quota.multipliers || [];
                          // Slice the simulated ARR by multiplier tiers using the SAME algorithm as backend
                          // _compute_deal_commission_with_boost, so the estimator matches the Commissions page.
                          const computeSimBoost = () => {
                            if (simTotal <= 0 || commRate <= 0) return { base: 0, boost: 0 };
                            const simBase = simTotal * (commRate / 100);
                            if (currentQQuota <= 0 || !multipliers.length) return { base: simBase, boost: 0 };
                            const tiers = [...multipliers]
                              .map(m => ({ threshold: parseFloat(m.threshold) || 0, multiplier: parseFloat(m.multiplier) || 1 }))
                              .sort((a, b) => a.threshold - b.threshold);
                            const breakpoints = tiers.map(t => (t.threshold / 100) * currentQQuota);
                            const start = currentQClosed;
                            const end = currentQClosed + simTotal;
                            let cursor = start;
                            let boost = 0;
                            while (cursor < end) {
                              const progressPct = currentQQuota > 0 ? (cursor / currentQQuota) * 100 : 0;
                              // Find the next breakpoint strictly greater than cursor
                              let nextBp = end;
                              for (const bp of breakpoints) {
                                if (bp > cursor) { nextBp = Math.min(nextBp, bp); break; }
                              }
                              const sliceArr = Math.min(nextBp, end) - cursor;
                              // Highest multiplier whose threshold <= progressPct
                              let mVal = 1;
                              for (const t of tiers) {
                                if (progressPct >= t.threshold) mVal = t.multiplier;
                              }
                              if (mVal > 1) boost += sliceArr * (commRate / 100) * (mVal - 1);
                              cursor = Math.min(nextBp, end);
                            }
                            return { base: simBase, boost };
                          };
                          const { base: simBase, boost: simBoost } = computeSimBoost();
                          // Total projected = commission already earned (real deals + spiffs) + delta from sim
                          const alreadyEarned = quota.commission_breakdown?.total || 0;
                          const projectedCommission = alreadyEarned + simBase + simBoost;
                          const activeTier = (() => {
                            const sorted = [...multipliers].map(m => ({ threshold: parseFloat(m.threshold) || 0, multiplier: parseFloat(m.multiplier) || 1 })).sort((a, b) => a.threshold - b.threshold);
                            let m = 1;
                            for (const t of sorted) { if (projectedQProgress >= t.threshold) m = t.multiplier; }
                            return m;
                          })();
                          
                          return (
                            <div className="px-4 pb-4 border-t border-slate-100">
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3 mb-3">
                                <div className="bg-violet-50 rounded-lg p-2.5 border border-violet-100">
                                  <p className="text-[10px] font-bold uppercase tracking-wider text-violet-500 mb-0.5">{currentQ?.label?.split(' ')[0]} Projected</p>
                                  <p className="text-base font-black text-violet-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{projectedQProgress}%</p>
                                  <p className="text-[10px] text-violet-500">${projectedQClosed.toLocaleString()} / ${currentQQuota.toLocaleString()}</p>
                                </div>
                                <div className="bg-indigo-50 rounded-lg p-2.5 border border-indigo-100">
                                  <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-500 mb-0.5">{quota.fy_label} Projected</p>
                                  <p className="text-base font-black text-indigo-700" style={{ fontFamily: 'Outfit, sans-serif' }}>{projectedAnnualProgress}%</p>
                                  <p className="text-[10px] text-indigo-500">${projectedAnnual.toLocaleString()} / ${quota.annual_quota.toLocaleString()}</p>
                                </div>
                                <div className="bg-amber-50 rounded-lg p-2.5 border border-amber-100">
                                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-600 mb-0.5">Gap to {currentQ?.label?.split(' ')[0]}</p>
                                  <p className="text-base font-black text-amber-700" style={{ fontFamily: 'Outfit, sans-serif' }}>${Math.max(0, gapToQuarterQuota - simTotal).toLocaleString()}</p>
                                  <p className="text-[10px] text-amber-500">{gapToQuarterQuota - simTotal <= 0 ? 'On target!' : 'remaining'}</p>
                                </div>
                                <div className="bg-emerald-50 rounded-lg p-2.5 border border-emerald-100" data-testid="sim-commission-tile">
                                  <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 mb-0.5">Est. Commission</p>
                                  <p className="text-base font-black text-emerald-700" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="sim-commission-total">${Math.round(projectedCommission).toLocaleString()}</p>
                                  <p className="text-[10px] text-emerald-600" data-testid="sim-commission-rate">at {commRate}% · {activeTier > 1 ? `${activeTier}× tier` : 'no boost yet'}</p>
                                </div>
                              </div>

                              {simTotal > 0 && (
                                <div className="mb-3 bg-gradient-to-r from-emerald-50 via-violet-50 to-white border border-emerald-100 rounded-lg px-3 py-2" data-testid="sim-commission-breakdown">
                                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Commission Breakdown (simulated deals only)</p>
                                  <div className="flex items-center gap-3 flex-wrap text-xs">
                                    <div>
                                      <span className="text-slate-500">Base ({commRate}%):</span>{' '}
                                      <span className="font-bold text-emerald-700" data-testid="sim-base">${Math.round(simBase).toLocaleString()}</span>
                                    </div>
                                    <div>
                                      <span className="text-slate-500">Multiplier boost:</span>{' '}
                                      <span className={`font-bold ${simBoost > 0 ? 'text-violet-700' : 'text-slate-400'}`} data-testid="sim-boost">${Math.round(simBoost).toLocaleString()}</span>
                                    </div>
                                    <div className="ml-auto">
                                      <span className="text-slate-500">Already earned:</span>{' '}
                                      <span className="font-bold text-slate-700">${Math.round(alreadyEarned).toLocaleString()}</span>
                                    </div>
                                  </div>
                                  {multipliers.length > 0 && (
                                    <p className="text-[10px] text-slate-500 mt-1">
                                      Tiers: {[...multipliers].sort((a, b) => (parseFloat(a.threshold) || 0) - (parseFloat(b.threshold) || 0)).map(m => `${m.threshold}%→${m.multiplier}×`).join(', ')}
                                    </p>
                                  )}
                                </div>
                              )}

                              {allAccounts && allAccounts.length > 0 && (
                                <div className="mb-2">
                                  <p className="text-[10px] text-slate-400 font-semibold mb-1.5">FILTER BY STAGE</p>
                                  <div className="flex flex-wrap gap-1 mb-2">
                                    {stages.filter(s => {
                                      const n = s.name.toLowerCase();
                                      return !n.includes('closed won') && !n.includes('closed lost');
                                    }).map(s => {
                                      const isActive = simStageFilter.includes(s.id);
                                      return (
                                        <button
                                          key={s.id}
                                          data-testid={`sim-stage-filter-${s.id}`}
                                          onClick={() => setSimStageFilter(prev => isActive ? prev.filter(id => id !== s.id) : [...prev, s.id])}
                                          className={`px-2 py-1 rounded-md text-[10px] font-semibold transition-all border ${
                                            isActive
                                              ? 'bg-violet-600 text-white border-violet-600'
                                              : 'bg-white text-slate-500 border-slate-200 hover:border-violet-300'
                                          }`}
                                        >
                                          {s.name}
                                        </button>
                                      );
                                    })}
                                    {simStageFilter.length > 0 && (
                                      <button onClick={() => setSimStageFilter([])} className="text-[10px] text-red-500 hover:text-red-600 font-semibold px-1">Clear</button>
                                    )}
                                  </div>
                                  <p className="text-[10px] text-slate-400 font-semibold mb-1.5">ADD FROM PIPELINE</p>
                                  <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                                    {allAccounts
                                      .filter(a => {
                                        const closedWonStage = stages.find(s => s.name === 'Closed Won');
                                        const closedLostStage = stages.find(s => s.name === 'Closed Lost');
                                        if (a.stage_id === closedWonStage?.id || a.stage_id === closedLostStage?.id) return false;
                                        if (simDeals.find(d => d.pipelineId === a.id)) return false;
                                        if (simStageFilter.length > 0 && !simStageFilter.includes(a.stage_id)) return false;
                                        return true;
                                      })
                                      .map(a => (
                                        <button
                                          key={a.id}
                                          data-testid={`sim-add-pipeline-${a.id}`}
                                          onClick={() => setSimDeals([...simDeals, { id: Date.now().toString(), name: a.account_name, arr: a.arr || 0, pipelineId: a.id }])}
                                          className="px-2 py-1 bg-slate-100 hover:bg-violet-100 hover:text-violet-700 text-slate-600 rounded-md text-xs font-medium transition-colors truncate max-w-[180px]"
                                          title={`${a.account_name} — $${(a.arr || 0).toLocaleString()} (${a.stage_name})`}
                                        >
                                          + {a.account_name} <span className="text-[9px] text-slate-400">${(a.arr || 0).toLocaleString()}</span>
                                        </button>
                                      ))}
                                    {allAccounts.filter(a => {
                                      const closedWonStage = stages.find(s => s.name === 'Closed Won');
                                      const closedLostStage = stages.find(s => s.name === 'Closed Lost');
                                      if (a.stage_id === closedWonStage?.id || a.stage_id === closedLostStage?.id) return false;
                                      if (simDeals.find(d => d.pipelineId === a.id)) return false;
                                      if (simStageFilter.length > 0 && !simStageFilter.includes(a.stage_id)) return false;
                                      return true;
                                    }).length === 0 && (
                                      <p className="text-[10px] text-slate-400">No deals in selected stages</p>
                                    )}
                                  </div>
                                </div>
                              )}

                              {simDeals.length > 0 && (
                                <div className="space-y-1.5 mb-2">
                                  <div className="flex items-center justify-between">
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Simulated Wins</p>
                                    <button data-testid="sim-clear-all" onClick={() => setSimDeals([])} className="text-xs text-red-500 hover:text-red-600 font-semibold">Clear all</button>
                                  </div>
                                  {simDeals.map((deal) => (
                                    <div key={deal.id} className="flex items-center gap-2 bg-violet-50 rounded-lg px-3 py-2 border border-violet-100">
                                      <span className="text-sm font-medium text-slate-700 flex-1 truncate">{deal.name}</span>
                                      <span className="text-xs text-slate-400 mr-1">$</span>
                                      <input
                                        type="number"
                                        data-testid={`sim-deal-arr-${deal.id}`}
                                        value={deal.arr}
                                        onChange={(e) => setSimDeals(simDeals.map(d => d.id === deal.id ? { ...d, arr: e.target.value } : d))}
                                        className="w-28 px-2 py-1 border border-violet-200 rounded-md text-sm text-right bg-white focus:outline-none focus:border-indigo-500"
                                      />
                                      <button data-testid={`sim-remove-${deal.id}`} onClick={() => setSimDeals(simDeals.filter(d => d.id !== deal.id))} className="p-1 text-slate-400 hover:text-red-500 transition-colors">
                                        <X size={14} />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  data-testid="sim-deal-name-input"
                                  placeholder="Type deal name + Enter..."
                                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-indigo-500"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && e.target.value.trim()) {
                                      setSimDeals([...simDeals, { id: Date.now().toString(), name: e.target.value.trim(), arr: 0 }]);
                                      e.target.value = '';
                                    }
                                  }}
                                />
                              </div>

                              <div className="mt-3">
                                <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
                                  <span>{currentQ?.label?.split(' ')[0]} Progress</span>
                                  <span>{projectedQProgress}% of ${currentQQuota.toLocaleString()}</span>
                                </div>
                                <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden relative">
                                  <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{ width: `${Math.min((currentQClosed / (currentQQuota || 1)) * 100, 100)}%` }} />
                                  {simTotal > 0 && (
                                    <div className="h-full bg-violet-400 rounded-r-full absolute top-0 transition-all duration-500"
                                      style={{
                                        left: `${Math.min((currentQClosed / (currentQQuota || 1)) * 100, 100)}%`,
                                        width: `${Math.min((simTotal / (currentQQuota || 1)) * 100, 100 - Math.min((currentQClosed / (currentQQuota || 1)) * 100, 100))}%`
                                      }}
                                    />
                                  )}
                                </div>
                                <div className="flex items-center gap-4 mt-2 text-[10px]">
                                  <div className="flex items-center gap-1">
                                    <span className="w-2.5 h-2.5 rounded-sm bg-indigo-500" />
                                    <span className="text-slate-500">Actual (${currentQClosed.toLocaleString()})</span>
                                  </div>
                                  {simTotal > 0 && (
                                    <div className="flex items-center gap-1">
                                      <span className="w-2.5 h-2.5 rounded-sm bg-violet-400" />
                                      <span className="text-slate-500">Simulated (+${simTotal.toLocaleString()})</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* Commission Tracker Card */}

                    {/* Annual Progress - Inverted Dark Theme */}
                    <div className="bg-slate-900 rounded-2xl p-6 mt-4">
                      <div className="flex flex-col md:flex-row gap-6 items-center">
                        {/* Annual Gauge */}
                        <div className="flex-shrink-0" data-testid="annual-gauge-meter">
                          <div className="relative w-48 h-32">
                            <svg viewBox="0 0 200 120" className="w-full h-full">
                              <path d="M 20 110 A 80 80 0 0 1 180 110" fill="none" stroke="#334155" strokeWidth="16" strokeLinecap="round" />
                              <path d="M 20 110 A 80 80 0 0 1 180 110" fill="none"
                                stroke={quota.annual_progress >= 100 ? '#34d399' : quota.annual_progress >= 75 ? '#a78bfa' : quota.annual_progress >= 50 ? '#c4b5fd' : '#fbbf24'}
                                strokeWidth="16" strokeLinecap="round"
                                strokeDasharray={`${Math.min(quota.annual_progress, 120) / 120 * 251.2} 251.2`}
                                style={{ transition: 'stroke-dasharray 1s ease-out' }}
                              />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-end pb-4">
                              <span className="text-3xl font-black" style={{
                                fontFamily: 'Outfit, sans-serif',
                                color: quota.annual_progress >= 100 ? '#34d399' : quota.annual_progress >= 75 ? '#a78bfa' : '#f1f5f9'
                              }}>
                                {Math.round(quota.annual_progress)}%
                              </span>
                              <span className="text-[10px] text-slate-400 font-medium">of {quota.fy_label} quota</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex-1 w-full">
                          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-3 gap-3">
                            <div>
                              <p className="text-xs font-bold uppercase tracking-wider text-violet-400 mb-1">
                                {quota.fy_label} Annual Progress
                                {!quota.is_current_fy && <span className="ml-2 text-[10px] bg-slate-700 text-slate-300 px-2 py-0.5 rounded-full">Historical</span>}
                              </p>
                              <p className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {formatCurrency(quota.annual_closed_won)}
                              </p>
                            </div>
                            <div className="text-left sm:text-right">
                              <p className="text-sm text-slate-400 mb-1">Annual Target</p>
                              <p className="text-xl font-bold text-slate-300" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {formatCurrency(quota.annual_quota)}
                              </p>
                            </div>
                          </div>
                          <div className="relative">
                            <div className="w-full h-4 bg-slate-700 rounded-full overflow-hidden">
                              <div
                                data-testid="quota-progress-bar-annual"
                                className={`h-full rounded-full transition-all duration-700 ease-out ${
                                  quota.annual_progress >= 100 ? 'bg-emerald-400' :
                                  quota.annual_progress >= 75 ? 'bg-violet-400' :
                                  quota.annual_progress >= 50 ? 'bg-violet-500' :
                                  'bg-amber-400'
                                }`}
                                style={{ width: `${Math.min(quota.annual_progress, 100)}%` }}
                              />
                            </div>
                            <div className="flex items-center justify-between mt-2">
                              <span className={`text-sm font-bold ${
                                quota.annual_progress >= 100 ? 'text-emerald-400' :
                                quota.annual_progress >= 75 ? 'text-violet-400' :
                                'text-slate-400'
                              }`}>
                                {Math.round(quota.annual_progress)}% of annual quota
                              </span>
                              <span className="text-sm text-slate-500">
                                {quota.annual_progress >= 100
                                  ? `${formatCurrency(quota.annual_closed_won - quota.annual_quota)} over target`
                                  : `${formatCurrency(quota.annual_quota - quota.annual_closed_won)} remaining`
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Quarter Breakdown */}
                    {quota.all_quarters && quota.all_quarters.length > 0 && (
                      <div className="border-t border-slate-200 pt-5">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Quarter Breakdown</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {quota.all_quarters.map((q) => (
                            <div
                              key={q.key}
                              data-testid={`quarter-card-${q.key}`}
                              className={`p-3 rounded-xl border transition-all ${
                                q.is_current
                                  ? 'border-indigo-300 bg-indigo-50 ring-1 ring-indigo-200'
                                  : 'border-slate-200 bg-slate-50'
                              }`}
                            >
                              <div className="flex items-center justify-between mb-1">
                                <span className={`text-xs font-bold uppercase ${q.is_current ? 'text-indigo-600' : 'text-slate-500'}`}>
                                  {q.key.toUpperCase()}
                                </span>
                                {q.is_current && (
                                  <span className="text-[10px] font-bold bg-indigo-600 text-white px-1.5 py-0.5 rounded-full">NOW</span>
                                )}
                              </div>
                              <p className="text-sm font-bold text-slate-900">{formatCurrency(q.closed_won)}</p>
                              <p className="text-xs text-slate-500">of {formatCurrency(q.quota)}</p>
                              {q.historical_closed_won > 0 && (
                                <p className="text-[10px] text-slate-400 mt-0.5">
                                  {formatCurrency(q.tracked_closed_won)} tracked + {formatCurrency(q.historical_closed_won)} prior
                                </p>
                              )}
                              <div className="w-full h-1.5 bg-slate-200 rounded-full mt-2 overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${
                                    q.progress >= 100 ? 'bg-emerald-500' : q.is_current ? 'bg-indigo-500' : 'bg-slate-400'
                                  }`}
                                  style={{ width: `${Math.min(q.progress, 100)}%` }}
                                />
                              </div>
                              <p className="text-[10px] font-semibold mt-1 text-slate-500">{Math.round(q.progress)}%</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Target size={32} className="mx-auto mb-3 text-slate-300" />
                    <p className="text-slate-500 mb-1">No quota set yet</p>
                    <button
                      onClick={() => setShowQuotaSettings(true)}
                      className="text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                    >
                      Set your quota targets
                    </button>
                  </div>
                )}
              </div>

              {/* Quota Simulator moved to inside Quota Tracker above */}

            </>
            )}
          </div>
        </div>
      </div>
    </div>

      <Dialog open={showAddLink} onOpenChange={(open) => { setShowAddLink(open); if (!open) { setEditingLink(null); setLinkForm({ name: '', url: '' }); } }}>
        <DialogContent className="rounded-2xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{editingLink ? 'Edit Link' : 'Add Quick Link'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-xs text-slate-500">Name</Label>
              <Input data-testid="link-name-input" value={linkForm.name} onChange={(e) => setLinkForm({...linkForm, name: e.target.value})} placeholder="e.g. My Calendar" className="mt-1 rounded-lg" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">URL</Label>
              <Input data-testid="link-url-input" value={linkForm.url} onChange={(e) => setLinkForm({...linkForm, url: e.target.value})} placeholder="e.g. calendar.google.com" className="mt-1 rounded-lg" />
            </div>
            <Button data-testid="save-link-btn" onClick={handleSaveLink} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">
              {editingLink ? 'Update Link' : 'Add Link'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showCardConfig} onOpenChange={setShowCardConfig}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Customize Dashboard Cards</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 mb-3">Pick which pipeline stages count toward each top card. Leave a card empty to use the smart default.</p>
          <div className="space-y-5">
            {[
              { key: 'total_pipeline', label: 'Total Pipeline ($)', hint: 'Default: all stages except Closed Lost' },
              { key: 'deals_in_play', label: 'Deals in Play (count)', hint: 'Default: all open stages (no Closed Won / Closed Lost)' },
              { key: 'closed_won', label: 'Closed Won ($)', hint: 'Default: Closed Won stages, current quarter only' },
            ].map(card => {
              const selected = new Set(cardConfigDraft[card.key] || []);
              const toggle = (sid) => {
                const next = new Set(selected);
                if (next.has(sid)) next.delete(sid); else next.add(sid);
                setCardConfigDraft({ ...cardConfigDraft, [card.key]: Array.from(next) });
              };
              return (
                <div key={card.key} data-testid={`card-config-${card.key}`} className="rounded-lg border border-slate-200 p-3 bg-white">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm font-bold text-slate-900">{card.label}</p>
                    {selected.size > 0 && (
                      <button
                        data-testid={`card-config-reset-${card.key}`}
                        onClick={() => setCardConfigDraft({ ...cardConfigDraft, [card.key]: [] })}
                        className="text-[10px] text-red-500 hover:text-red-700 font-semibold uppercase tracking-wider"
                      >Reset to default</button>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mb-2">{card.hint}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(stats.stats || []).map(s => (
                      <button
                        key={s.stage_id}
                        data-testid={`card-config-stage-${card.key}-${s.stage_id}`}
                        onClick={() => toggle(s.stage_id)}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${selected.has(s.stage_id) ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'}`}
                      >
                        {s.stage_name}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-3 justify-end pt-4">
            <Button variant="outline" onClick={() => setShowCardConfig(false)} className="rounded-lg border-slate-200">Cancel</Button>
            <Button
              data-testid="save-card-config-btn"
              onClick={async () => {
                try {
                  const cleaned = {};
                  Object.keys(cardConfigDraft).forEach(k => { if ((cardConfigDraft[k] || []).length) cleaned[k] = cardConfigDraft[k]; });
                  const { data } = await axios.put(`${API_URL}/api/dashboard/card-config`, cleaned, { withCredentials: true });
                  setCardConfig(data.dashboard_card_config || {});
                  toast.success('Dashboard cards updated');
                  setShowCardConfig(false);
                } catch { toast.error('Failed to save card config'); }
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
            >Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showQuotaSettings} onOpenChange={setShowQuotaSettings}>
        <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Quota Settings {quota?.fy_label ? `— ${quota.fy_label}` : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 block">Quarterly Configuration</Label>
              <div className="space-y-3">
                {['q1','q2','q3','q4'].map((qk, i) => {
                  const qLabel = quota?.all_quarters?.[i]?.label || `Q${i+1}`;
                  const isCurrent = quota?.all_quarters?.[i]?.is_current;
                  const hKey = `h${i+1}`;
                  return (
                    <div key={qk} className={`p-3 rounded-xl border ${isCurrent ? 'border-indigo-300 bg-indigo-50/50' : 'border-slate-200 bg-slate-50/50'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs font-bold uppercase ${isCurrent ? 'text-indigo-600' : 'text-slate-500'}`}>{qLabel}</span>
                        {isCurrent && <span className="text-[10px] font-bold bg-indigo-600 text-white px-1.5 py-0.5 rounded-full">CURRENT</span>}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-[11px] text-slate-500">Quota Target ($)</Label>
                          <Input data-testid={`quota-${qk}-input`} type="number" value={quotaForm[qk]} onChange={(e) => setQuotaForm({...quotaForm, [qk]: e.target.value})} placeholder="e.g. 250000" className="mt-1 rounded-lg border-slate-200 text-sm" />
                        </div>
                        <div>
                          <Label className="text-[11px] text-slate-500">Prior Attainment ($)</Label>
                          <Input data-testid={`historical-${qk}-input`} type="number" value={quotaForm[hKey]} onChange={(e) => setQuotaForm({...quotaForm, [hKey]: e.target.value})} placeholder="Historical closed won" className="mt-1 rounded-lg border-slate-200 text-sm" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-xs text-slate-400">
                <span>Annual quota: {formatCurrency((parseFloat(quotaForm.q1) || 0) + (parseFloat(quotaForm.q2) || 0) + (parseFloat(quotaForm.q3) || 0) + (parseFloat(quotaForm.q4) || 0))}</span>
                <span>Historical total: {formatCurrency((parseFloat(quotaForm.h1) || 0) + (parseFloat(quotaForm.h2) || 0) + (parseFloat(quotaForm.h3) || 0) + (parseFloat(quotaForm.h4) || 0))}</span>
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Fiscal Year Starts</Label>
              <Select value={quotaForm.fiscal_year_start_month} onValueChange={(v) => setQuotaForm({...quotaForm, fiscal_year_start_month: v})}>
                <SelectTrigger data-testid="fy-start-month-select" className="mt-2 rounded-lg border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                    <SelectItem key={i+1} value={(i+1).toString()}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Quarter Length (months)</Label>
              <Select value={quotaForm.quarter_months} onValueChange={(v) => setQuotaForm({...quotaForm, quarter_months: v})}>
                <SelectTrigger data-testid="quarter-length-select" className="mt-2 rounded-lg border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  <SelectItem value="3">3 months (standard)</SelectItem>
                  <SelectItem value="4">4 months</SelectItem>
                  <SelectItem value="6">6 months (semi-annual)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <h3 className="text-sm font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Default Commission Setup
              </h3>
              <p className="text-xs text-slate-500 mb-3">Applied to every quarter unless overridden below. Multiplier tiers apply <em>only</em> to the ARR slice that falls within each bracket (e.g. 100&#8211;130% at 1.3&times;), not to the full deal.</p>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-3">
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Base rate %</Label>
                  <Input
                    data-testid="global-commission-rate"
                    type="number"
                    step="0.1"
                    placeholder="e.g. 24"
                    value={quotaForm.commission_rate ?? ''}
                    onChange={(e) => setQuotaForm({ ...quotaForm, commission_rate: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0 })}
                    className="mt-1 rounded-md border-slate-200 text-sm"
                  />
                </div>
              </div>
              <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Multiplier Tiers</Label>
              <div className="mt-1 space-y-2">
                {(quotaForm.multipliers || []).map((m, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`global-mult-${i}`}>
                    <span className="text-[11px] text-slate-500">At</span>
                    <Input
                      data-testid={`global-mult-threshold-${i}`}
                      type="number"
                      step="1"
                      placeholder="Threshold %"
                      value={m.threshold}
                      onChange={(e) => {
                        const u = [...(quotaForm.multipliers || [])];
                        u[i] = { ...u[i], threshold: parseFloat(e.target.value) || 0 };
                        setQuotaForm({ ...quotaForm, multipliers: u });
                      }}
                      className="w-24 rounded-md border-slate-200 text-xs h-8"
                    />
                    <span className="text-[11px] text-slate-500">% attainment, apply</span>
                    <Input
                      data-testid={`global-mult-value-${i}`}
                      type="number"
                      step="0.05"
                      placeholder="Multiplier"
                      value={m.multiplier}
                      onChange={(e) => {
                        const u = [...(quotaForm.multipliers || [])];
                        u[i] = { ...u[i], multiplier: parseFloat(e.target.value) || 1 };
                        setQuotaForm({ ...quotaForm, multipliers: u });
                      }}
                      className="w-20 rounded-md border-slate-200 text-xs h-8"
                    />
                    <span className="text-[11px] text-slate-500">&times;</span>
                    <button
                      onClick={() => setQuotaForm({ ...quotaForm, multipliers: (quotaForm.multipliers || []).filter((_, j) => j !== i) })}
                      className="p-1.5 text-slate-400 hover:text-red-600 rounded"
                      data-testid={`global-mult-remove-${i}`}
                      title="Remove tier"
                    >
                      <Trash size={12} />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setQuotaForm({ ...quotaForm, multipliers: [...(quotaForm.multipliers || []), { threshold: 100, multiplier: 1.3 }] })}
                  className="text-xs text-indigo-600 hover:text-indigo-800 font-semibold flex items-center gap-1"
                  data-testid="global-mult-add"
                >
                  <Plus size={12} /> Add tier
                </button>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-4" data-testid="per-quarter-commission-section">
              <h3 className="text-sm font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Commission by Quarter
              </h3>
              <p className="text-xs text-slate-500 mb-3">Set the rate &amp; multiplier tiers for each quarter. Empty = 0% for that quarter.</p>
              <div className="space-y-3">
                {['q1', 'q2', 'q3', 'q4'].map(qKey => {
                  const fullKey = `${selectedFy}_${qKey}`;
                  const override = quotaForm.quarterly_commission_overrides?.[fullKey] || {};
                  const setOverride = (patch) => {
                    const next = { ...quotaForm.quarterly_commission_overrides };
                    const newQ = { ...(next[fullKey] || {}), ...patch };
                    if (newQ.commission_rate === undefined && (!newQ.multipliers || newQ.multipliers.length === 0)) {
                      delete next[fullKey];
                    } else {
                      next[fullKey] = newQ;
                    }
                    setQuotaForm({ ...quotaForm, quarterly_commission_overrides: next });
                  };
                  const qLabel = (quota?.all_quarters || []).find(q => q.key === qKey)?.label || qKey.toUpperCase();
                  return (
                    <div key={qKey} data-testid={`quarter-override-${qKey}`} className="rounded-lg border border-slate-200 bg-white p-3">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-bold text-slate-700">{qLabel}</p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <div>
                          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Rate %</Label>
                          <Input
                            data-testid={`override-rate-${qKey}`}
                            type="number"
                            step="0.1"
                            placeholder="0"
                            value={override.commission_rate ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              setOverride({ commission_rate: v === '' ? undefined : (parseFloat(v) || 0) });
                            }}
                            className="mt-1 rounded-md border-slate-200 text-xs h-8"
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Multiplier Tiers</Label>
                          {(override.multipliers || []).map((m, i) => (
                            <div key={i} className="flex gap-1 items-center mt-1">
                              <Input
                                data-testid={`override-mult-threshold-${qKey}-${i}`}
                                type="number"
                                placeholder="%"
                                value={m.threshold}
                                onChange={(e) => {
                                  const u = [...(override.multipliers || [])];
                                  u[i] = { ...u[i], threshold: parseFloat(e.target.value) || 0 };
                                  setOverride({ multipliers: u });
                                }}
                                className="rounded-md border-slate-200 text-xs h-7 w-16"
                              />
                              <span className="text-[10px] text-slate-400">at</span>
                              <Input
                                data-testid={`override-mult-value-${qKey}-${i}`}
                                type="number"
                                step="0.1"
                                placeholder="x"
                                value={m.multiplier}
                                onChange={(e) => {
                                  const u = [...(override.multipliers || [])];
                                  u[i] = { ...u[i], multiplier: parseFloat(e.target.value) || 1 };
                                  setOverride({ multipliers: u });
                                }}
                                className="rounded-md border-slate-200 text-xs h-7 w-16"
                              />
                              <span className="text-[10px] text-slate-400">x</span>
                              <button
                                onClick={() => setOverride({ multipliers: (override.multipliers || []).filter((_, j) => j !== i) })}
                                className="text-red-400 hover:text-red-600"
                              ><X size={12} /></button>
                            </div>
                          ))}
                          <button
                            data-testid={`add-override-mult-${qKey}`}
                            onClick={() => setOverride({ multipliers: [...(override.multipliers || []), { threshold: 100, multiplier: 1.3 }] })}
                            className="text-[10px] text-indigo-600 hover:text-indigo-700 font-semibold mt-1"
                          >+ Add tier</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <p className="text-xs text-slate-500">
                Spiff bonuses are now managed on the <a href="/commissions" className="text-indigo-600 hover:underline font-semibold">Commissions tab</a>.
              </p>
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <Button variant="outline" onClick={() => setShowQuotaSettings(false)} className="rounded-lg border-slate-200">Cancel</Button>
              <Button data-testid="save-quota-btn" onClick={handleSaveQuota} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">Save Settings</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAddDeal} onOpenChange={setShowAddDeal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Add New Deal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Account Name *</Label>
              <Input data-testid="new-deal-name" value={newDeal.account_name} onChange={(e) => setNewDeal({...newDeal, account_name: e.target.value})} placeholder="Company Name" className="mt-1 rounded-lg border-slate-200" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Pipeline Stage *</Label>
              <Select value={newDeal.stage_id} onValueChange={(v) => setNewDeal({...newDeal, stage_id: v})}>
                <SelectTrigger data-testid="new-deal-stage" className="mt-1 rounded-lg border-slate-200"><SelectValue placeholder="Select stage" /></SelectTrigger>
                <SelectContent className="rounded-lg">
                  {stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {pricingTiers.length > 0 ? (
              <div data-testid="new-deal-pricing-lines">
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Pricing</Label>
                <div className="space-y-2">
                  {(newDeal.pricing_lines.length > 0 ? newDeal.pricing_lines : pricingTiers.map(t => ({ tier_name: t.name, quantity: '', rate: t.default_rate.toString(), rate_type: t.rate_type }))).map((line, i) => (
                    <div key={i} className="flex items-center gap-2 bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                      <span className="text-xs font-medium text-slate-700 w-28 truncate">{line.tier_name}</span>
                      {line.rate_type !== 'flat' && (
                        <Input
                          data-testid={`new-deal-line-qty-${i}`}
                          type="number"
                          value={line.quantity}
                          onChange={(e) => {
                            const lines = newDeal.pricing_lines.length > 0 ? [...newDeal.pricing_lines] : pricingTiers.map(t => ({ tier_name: t.name, quantity: '', rate: t.default_rate.toString(), rate_type: t.rate_type }));
                            lines[i] = { ...lines[i], quantity: e.target.value };
                            setNewDeal({...newDeal, pricing_lines: lines});
                          }}
                          placeholder="0"
                          className="w-20 rounded-lg border-slate-200 text-sm"
                        />
                      )}
                      <span className="text-xs text-slate-400">@</span>
                      <div className="flex items-center gap-0.5">
                        <span className="text-xs text-slate-400">$</span>
                        <Input
                          data-testid={`new-deal-line-rate-${i}`}
                          type="number"
                          step="0.01"
                          value={line.rate}
                          onChange={(e) => {
                            const lines = newDeal.pricing_lines.length > 0 ? [...newDeal.pricing_lines] : pricingTiers.map(t => ({ tier_name: t.name, quantity: '', rate: t.default_rate.toString(), rate_type: t.rate_type }));
                            lines[i] = { ...lines[i], rate: e.target.value };
                            setNewDeal({...newDeal, pricing_lines: lines});
                          }}
                          placeholder="0.00"
                          className="w-20 rounded-lg border-slate-200 text-sm"
                        />
                        <span className="text-[10px] text-slate-400">/mo</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Employee Count *</Label>
                  <Input data-testid="new-deal-employees" type="number" value={newDeal.employee_count} onChange={(e) => setNewDeal({...newDeal, employee_count: e.target.value})} placeholder="50" className="mt-1 rounded-lg border-slate-200" />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Rate/Employee (Monthly) *</Label>
                  <Input data-testid="new-deal-rate" type="number" step="0.01" value={newDeal.per_employee_rate} onChange={(e) => setNewDeal({...newDeal, per_employee_rate: e.target.value})} placeholder="100.00" className="mt-1 rounded-lg border-slate-200" />
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Discount %</Label>
              <Input data-testid="new-deal-discount" type="number" step="0.1" value={newDeal.discount_percent} onChange={(e) => setNewDeal({...newDeal, discount_percent: e.target.value})} placeholder="0" className="mt-1 rounded-lg border-slate-200" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Assigned Partner</Label>
              <Input
                data-testid="new-deal-partner"
                list="dashboard-partners-list"
                value={newDeal.assigned_partner_name || ''}
                onChange={(e) => {
                  const name = e.target.value;
                  // Try to match against existing partners by company/name
                  const match = partners.find(p => ((p.company || p.name || '').toLowerCase() === name.trim().toLowerCase()));
                  setNewDeal({ ...newDeal, assigned_partner_name: name, assigned_partner_id: match ? match.id : '' });
                }}
                placeholder="Type a partner name or pick an existing one"
                className="mt-1 rounded-lg border-slate-200"
              />
              <datalist id="dashboard-partners-list">
                {partners.map(p => <option key={p.id} value={p.company || p.name} />)}
              </datalist>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Contact Name</Label>
                <Input value={newDeal.main_contact_name} onChange={(e) => setNewDeal({...newDeal, main_contact_name: e.target.value})} placeholder="John Doe" className="mt-1 rounded-lg border-slate-200" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Contact Email</Label>
                <Input type="email" value={newDeal.main_contact_email} onChange={(e) => setNewDeal({...newDeal, main_contact_email: e.target.value})} placeholder="john@company.com" className="mt-1 rounded-lg border-slate-200" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Notes</Label>
              <div className="mt-1">
                <RichTextEditor value={newDeal.notes} onChange={(v) => setNewDeal({...newDeal, notes: v})} placeholder="Add notes..." />
              </div>
            </div>
            <CustomFieldsRenderer
              fields={dealTemplate}
              values={newDeal.custom_fields || {}}
              onChange={(cf) => setNewDeal({ ...newDeal, custom_fields: cf })}
              idPrefix="new-deal-custom"
            />
            <div className="flex gap-3 justify-end pt-4">
              <Button variant="outline" onClick={() => setShowAddDeal(false)} className="rounded-lg border-slate-200">Cancel</Button>
              <Button data-testid="create-deal-submit" onClick={handleCreateDeal} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg">Create Deal</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <PipelinePulse open={showPulse} onOpenChange={setShowPulse} />
    </>
  );
};